import './App.css';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from './supabaseClient';
import logo from './assets/prosthetiq_logo.png';

const FEEDBACK_OPTIONS = [
  { value: 'likely', label: 'Likely to use again' },
  { value: 'consider', label: 'Would consider using again' },
  { value: 'not_likely', label: 'Not likely to use again' },
];

const CLINICAL_FILTER_OPTIONS = [
  { value: 'all', label: 'Clinical Filter' },
  { value: 'adult', label: 'Adult' },
  { value: 'pediatric', label: 'Pediatric' },
  { value: 'pdac_verified', label: 'PDAC Verified' },
  { value: 'suitable_outdoor', label: 'Suitable for Outdoor Use' },
  { value: 'water_resistant', label: 'Water-Resistant' },
  { value: 'weatherproof', label: 'Weatherproof' },
  { value: 'waterproof', label: 'Waterproof' },
  { value: 'special_use', label: 'Special Use' },
];

const KYAN_PROMPT_CHIPS = [
  { label: 'Stability / Balance Feet', prompt: 'K2 transtibial stability foot' },
  { label: 'Energy Return / Dynamic Response Feet', prompt: 'K3 transtibial energy return foot' },
  { label: 'Mobility Enhancement / Advanced Function Feet', prompt: 'K3 adaptive ankle foot' },
  { label: 'Shock-Absorbing Feet', prompt: 'K3 transtibial shock absorption foot' },
  { label: 'Maximum Stability / Safety Knees', prompt: 'K1 transfemoral safety knee' },
  { label: 'Gait Efficiency / Energy Conservation Knees', prompt: 'K2 transfemoral gait efficiency knee' },
  { label: 'Motor Control / Powered Knee Systems', prompt: 'transfemoral powered knee' },
];

function normalizeText(value) {
  return String(value || '')
    .replace(/"/g, '')
    .replace(/'/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

function normalizeCode(code) {
  return String(code || '')
    .replace(/"/g, '')
    .replace(/'/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

function formatUrl(url) {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function parseAnonymousComment(commentText) {
  const raw = String(commentText || '');
  const anonymousPrefix = '[ANON]';
  const isAnonymous = raw.startsWith(anonymousPrefix);

  return {
    isAnonymous,
    text: isAnonymous ? raw.replace(anonymousPrefix, '').trim() : raw,
  };
}

function buildStoredComment(commentText, isAnonymous) {
  const cleaned = String(commentText || '').trim();
  if (!cleaned) return '';
  return isAnonymous ? `[ANON] ${cleaned}` : cleaned;
}

function getUserDisplayName(userRow) {
  if (!userRow) return 'Clinician';

  return (
    userRow.display_name ||
    userRow.name ||
    userRow.Name ||
    userRow.full_name ||
    userRow['Full Name'] ||
    userRow.email ||
    userRow.Email ||
    'Clinician'
  );
}

function getVariantLCodes(variant) {
  const rawValue =
  variant?.['RECOMMENDED L-CODES'] ||
  variant?.['Recommended L-Codes'] ||
  variant?.['Recommended L-codes'] ||
  variant?.['L CODES'] ||
  variant?.['L-CODES'] ||
  variant?.['L-CODE'] ||
  variant?.['L CODE'] ||
  variant?.['HCPCS CODES'] ||
  '';

  if (!rawValue) return [];

  return String(rawValue)
    .split(/\s+OR\s+|[,;/]+/i)
    .map((code) => normalizeCode(code))
    .filter(Boolean);
}

function getLCodeOptionValue(row) {
  return String(
    row?.['L Codes'] ||
      row?.['L Code'] ||
      row?.['L-code'] ||
      row?.['L-Code'] ||
      row?.['L CODE'] ||
      row?.['L-CODE'] ||
      row?.['code'] ||
      row?.['Code'] ||
      row?.['value'] ||
      row?.['Value'] ||
      ''
  ).trim();
}

function getLCodeOptionDeviceType(row) {
  return normalizeText(
    row?.['Product Type'] ||
      row?.['PRODUCT TYPE'] ||
      row?.['product_type'] ||
      row?.['DEVICE TYPE'] ||
      row?.['Device Type'] ||
      row?.['device_type'] ||
      row?.['CATEGORY'] ||
      row?.['Category'] ||
      row?.['category'] ||
      ''
  );
}

function getWaterCategory(value) {
  const raw = normalizeText(value);

  if (!raw) return '';

  if (
    raw.includes('IP67') ||
    raw.includes('IP68') ||
    raw.includes('WATERPROOF') ||
    raw.includes('FRESH WATER')
  ) {
    return 'waterproof';
  }

  if (
    raw.includes('IP55') ||
    raw.includes('IP65') ||
    raw.includes('WEATHERPROOF')
  ) {
    return 'weatherproof';
  }

  if (
    raw.includes('IP34') ||
    raw.includes('IP44') ||
    raw.includes('IP45') ||
    raw.includes('IP54') ||
    raw.includes('WATER RESISTANT') ||
    raw.includes('WATER-RESISTANT') ||
    raw.includes('CORROSION RESISTANT')
  ) {
    return 'water_resistant';
  }

  if (
    raw.includes('IP22') ||
    raw.includes('IP24') ||
    raw.includes('OUTDOOR')
  ) {
    return 'suitable_outdoor';
  }

  return '';
}

function pageMatchesDeviceType(activePage, deviceType) {
  const normalized = normalizeText(deviceType);

  if (activePage === 'Feet') return normalized === 'FOOT';
  if (activePage === 'Knees') return normalized === 'KNEE';
  if (activePage === 'Hips') return normalized === 'HIP';
  if (activePage === 'Integrated Systems') {
    return normalized === 'INTEGRATED SYSTEM';
  }

  return true;
}

function getMaxUserWeightLbs(product) {
  return (
    product?.['DEFAULT MODERATE IMPACT MAX USER WEIGHT lbs'] ||
    product?.['HIGH IMPACT MAX USER WEIGHT lbs'] ||
    product?.['LOW IMPACT USER WEIGHT lbs'] ||
    'N/A'
  );
}

function extractWeightFromPrompt(prompt) {
  const text = String(prompt || '');

  const match = text.match(/(\d{2,3})\s*(lb|lbs|pounds)/i);

  return match ? Number(match[1]) : null;
}

function detectDeviceType(prompt) {
  const text = normalizeText(prompt);

  if (text.includes('FOOT') || text.includes('FEET')) return 'FOOT';
  if (text.includes('KNEE') || text.includes('KNEES')) return 'KNEE';
  if (text.includes('HIP') || text.includes('HIPS')) return 'HIP';
  if (text.includes('INTEGRATED')) return 'INTEGRATED SYSTEM';

  return '';
}
function detectFunctionalLevel(prompt) {
  const text = normalizeText(prompt);

  if (text.includes('K1')) return 'K1';
  if (text.includes('K2')) return 'K2';
  if (text.includes('K3')) return 'K3';
  if (text.includes('K4')) return 'K4';

  return '';
}
function detectAmpLevel(prompt) {
  const text = normalizeText(prompt);

  if (text.includes('TRANSTIBIAL') || text.includes('TT') || text.includes('BK')) {
    return 'TT';
  }

  if (text.includes('TRANSFEMORAL') || text.includes('TF') || text.includes('AK')) {
    return 'TF';
  }

  if (text.includes('KNEE DISARTICULATION') || text.includes('KD')) {
    return 'KD';
  }

  if (text.includes('HIP DISARTICULATION') || text.includes('HD')) {
    return 'HD';
  }

  if (text.includes('BILATERAL')) {
    return 'BILATERAL';
  }

  return '';
}
function detectClinicalIntent(prompt) {
  const text = normalizeText(prompt);

  // Unspecified / Support Codes
  if (
    text.includes('UNSPECIFIED') ||
    text.includes('SUPPORT') ||
    text.includes('ALIGNMENT') ||
    text.includes('PROTECTIVE COVER') ||
    text.includes('L5999') ||
    text.includes('L5910') ||
    text.includes('L5962')
  ) {
    return 'foot_unspecified';
  }

  // Shock Absorption
  if (
    text.includes('SHOCK') ||
    text.includes('SHOCK ABSORPTION') ||
    text.includes('IMPACT')
  ) {
    return 'shock_absorption';
  }

  // Energy Return / Dynamic Response
  if (
    text.includes('ENERGY') ||
    text.includes('DYNAMIC') ||
    text.includes('RESPONSE') ||
    text.includes('SPRING') ||
    text.includes('RUNNING')
  ) {
    return 'energy_return';
  }

  // Stability / Balance
  if (
    text.includes('STABILITY') ||
    text.includes('BALANCE') ||
    text.includes('SAFE') ||
    text.includes('SECURE')
  ) {
    return 'foot_stability';
  }

  // Advanced / Microprocessor / Powered
  if (
  text.includes('MICROPROCESSOR') ||
  text.includes('POWERED') ||
  text.includes('SMART') ||
  text.includes('ADAPTIVE') ||
  text.includes('ANKLE') ||
  text.includes('MPA')
) {
  return 'foot_advanced';
}

if (text.includes('KNEE') || text.includes('KNEES')) {
  return 'gait_efficiency';
}

// Fallback based on K-level when no clinical intent is specified
if (text.includes('K1') || text.includes('K2')) {
  return 'foot_stability';
}

if (text.includes('K3') || text.includes('K4')) {
  return 'energy_return';
}

return 'foot_stability';
}

const FOOT_KYAN_GROUPS = {
  shock_absorption: {
    title: 'Shock-Absorbing Feet',
    codes: ['L5987', 'L5988'],
    associatedCodes: 'L5987, L5988',
    description:
      'Base shock-absorbing foot category intended to reduce loading forces and improve comfort during gait.',
    refinements: ['standard', 'multiaxial', 'elevated_vacuum'],
  },
  energy_return: {
    title: 'Energy Return / Dynamic Response Feet',
    codes: ['L5981', 'L5980', 'L5976', 'L5979'],
    associatedCodes: 'L5981, L5980, L5976, L5979',
    description:
      'Foot category focused on energy return, dynamic response, rollover efficiency, and forward progression.',
    refinements: ['standard', 'multiaxial', 'elevated_vacuum', 'hydraulic', 'adjustable'],
  },
  foot_stability: {
    title: 'Stability / Balance Feet',
    codes: ['L5970', 'L5971', 'L5972', 'L5974'],
    associatedCodes: 'L5970, L5971, L5972, L5974',
    description:
      'Foot category focused on basic stability, controlled rollover, and safer ambulation.',
    refinements: ['standard', 'multiaxial', 'elevated_vacuum', 'hydraulic', 'adjustable'],
  },
  foot_advanced: {
    title: 'Mobility Enhancement / Advanced Function Feet',
    codes: ['L5973', 'L5969'],
    associatedCodes: 'L5973, L5969',
    description:
      'Advanced foot category including microprocessor or powered ankle-foot function.',
    refinements: ['standard', 'elevated_vacuum'],
  },
  foot_unspecified: {
    title: 'Unspecified Foot Codes',
    codes: ['L5999'],
	associatedCodes: 'L5999',
    description:
      'Support, alignment, protective cover, or unspecified foot-related coding.',
    refinements: [],
  },
};
const KNEE_KYAN_GROUPS = {
  maximum_stability: {
    title: 'Maximum Stability / Safety Knees',
    codes: ['L5810', 'L5811', 'L5812', 'L5814', 'L5816', 'L5818'],
    associatedCodes: 'L5810, L5811, L5812, L5814, L5816, L5818',
    description:
      'Knee category focused on maximum stance stability, safety, and controlled ambulation.',
    refinements: [
  	'standard',
  	'shock_absorption',
  	'variable_cadence',
  	'limb_advancement',
  	'manual_lock',
   ],
  },

  gait_efficiency: {    
	title: 'Gait Efficiency / Energy Conservation Knees',
    	  codes: ['L5611', 'L5613', 'L5615', 'L5616'],
    	  associatedCodes: 'L5611, L5613, L5615, L5616',
    	  description:
      	  'Knee category focused on smoother gait mechanics, reduced effort, and improved walking efficiency.',
    	refinements: [
      	  'shock_absorption',
      	  'variable_cadence',
      	  'limb_advancement',
      	  'microprocessor',
      	  'manual_lock',
      	  'integrated_system',
   	 ],
  	},

  variable_cadence: {
    title: 'Variable Cadence / Swing Assistance Knees',
    codes: ['L5822', 'L5824', 'L5826', 'L5828', 'L5830', 'L5840', 'L5841'],
    associatedCodes: 'L5822, L5824, L5826, L5828, L5830, L5840, L5841',
    description:
      'Knee category focused on swing phase control, cadence changes, and smoother limb progression during walking.',
    refinements: [
      'shock_absorption',
      'variable_cadence',
      'limb_advancement',
      'microprocessor',
      'manual_lock',
      'integrated_system',
    ],
  },

  motor_control: {
  title: 'Motor Control / Powered Knee Systems',
  codes: ['L5827', 'L5859'], 
  associatedCodes: 'L5827, L5856, L5859', 
  description:
    'Advanced knee category involving powered, motorized, or actively controlled knee function.',
  refinements: [], 
},

  knee_unspecified: {
    title: 'Unspecified / Other Knee Codes',
    codes: ['L5999'],
    associatedCodes: 'L5999',
    description:
     'Unspecified or miscellaneous knee-related coding that may require additional review.',
    refinements: [],
  },
};

const HIP_KYAN_GROUPS = {
  hip_stability: {
    title: 'Stability & Pelvic Control Hips',
    codes: ['L5331', 'L5341'],
    associatedCodes: 'L5331/L5341',
    description:
      'Hip category focused on pelvic control, stance stability, and safer hip-level ambulation.',
    refinements: [
      'flexion_assist',
      'hydraulic_control',
      'manual_lock',
    ],
  },
};

function App() {
  const productsPerPage = 24;

  const [activePage, setActivePage] = useState('Kyan');
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedJustification, setSelectedJustification] = useState(null);
  const [favoriteFamilyIds, setFavoriteFamilyIds] = useState([]);
  const [compareProductIds, setCompareProductIds] = useState([]);
  const [activeCompareLeftId, setActiveCompareLeftId] = useState(null);
  const [activeCompareRightId, setActiveCompareRightId] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [justifications, setJustifications] = useState([]);
  const [clinicalBenefits, setClinicalBenefits] = useState([]);
  const [expandedBenefit, setExpandedBenefit] = useState(null);

  const [studies, setStudies] = useState([]);
  const [clinicalBenefitStudies, setClinicalBenefitStudies] = useState([]);
  const [expandedEvidence, setExpandedEvidence] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [cameFromKyan, setCameFromKyan] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [kyanPrompt, setKyanPrompt] = useState('');
  const [expandedKyanGroup, setExpandedKyanGroup] = useState(null);
  const [kyanRefinements, setKyanRefinements] = useState({});
  const [kyanBaseCodeFilters, setKyanBaseCodeFilters] = useState({});
  const [clinicalFilter, setClinicalFilter] = useState('all');
  const [patientWeight, setPatientWeight] = useState('');
  const [lCodeFilter, setLCodeFilter] = useState('all');
  const [manufacturerFilter, setManufacturerFilter] = useState('all');
  const [lCodeOptionsTable, setLCodeOptionsTable] = useState([]);

  const [productRatings, setProductRatings] = useState([]);
  const [productComments, setProductComments] = useState([]);
  const [commentUsers, setCommentUsers] = useState({});
  const [selectedFeedback, setSelectedFeedback] = useState('');
  const [commentInput, setCommentInput] = useState('');
  const [submitAnonymous, setSubmitAnonymous] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [showNegativeModal, setShowNegativeModal] = useState(false);

  // removed unused session state
  const [authUser, setAuthUser] = useState(null);
  const [currentUserRow, setCurrentUserRow] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginMessage, setLoginMessage] = useState('');
  const [isSendingLogin, setIsSendingLogin] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const [showSignupForm, setShowSignupForm] = useState(false);
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupCertifyingBody, setSignupCertifyingBody] = useState('');
  const [signupCertificationNumber, setSignupCertificationNumber] = useState('');
  const [signupMessage, setSignupMessage] = useState('');
  const [isSubmittingSignup, setIsSubmittingSignup] = useState(false);

  const [pendingAction, setPendingAction] = useState(null);
  const [designSessionId, setDesignSessionId] = useState('');

  const currentUserId = currentUserRow?.id || null;
  const isLoggedIn = !!authUser && !!currentUserId;
  
const resetDetailState = useCallback(() => {
  setSelectedFamily(null);
  setSelectedVariant(null);
  setSelectedJustification(null);
  setClinicalBenefits([]);
  setExpandedBenefit(null);
  setProductRatings([]);
  setProductComments([]);
  setCommentUsers({});
  setSelectedFeedback('');
  setCommentInput('');
  setSubmitAnonymous(false);
  setShowNegativeModal(false);
}, []);

const goToPage = useCallback(
  (page) => {
    setActivePage(page);
    setCurrentPage(1);
    resetDetailState();
  },
  [resetDetailState]
);

const goToLoginPage = useCallback(
  (action = null) => {
    if (action) {
      setPendingAction(action);
      localStorage.setItem('prosthetiq_pending_action', JSON.stringify(action));
    }
    setActivePage('Login');
    resetDetailState();
  },
  [resetDetailState]
);

const handleProtectedPageNav = useCallback(
  (page) => {
    if (!isLoggedIn) {
      goToLoginPage({ type: 'page', page });
      return;
    }
    goToPage(page);
  },
  [isLoggedIn, goToLoginPage, goToPage]
);

const completePendingAction = useCallback(() => {
  const stored = localStorage.getItem('prosthetiq_pending_action');
  const action = pendingAction || (stored ? JSON.parse(stored) : null);

  if (!action) {
    goToPage('All Products');
    return;
  }

  localStorage.removeItem('prosthetiq_pending_action');
  setPendingAction(null);

  if (action.type === 'page' && action.page) {
    goToPage(action.page);
    return;
  }

  if (
    action.type === 'favorite' ||
    action.type === 'compare' ||
    action.type === 'feedback'
  ) {
    goToPage('All Products');
    return;
  }

  goToPage('All Products');
}, [pendingAction, goToPage]);
const checkApprovedUser = useCallback(async (user) => {
  if (!user?.email) return null;

  const loginEmail = String(user.email).trim().toLowerCase();

  const { data, error } = await supabase
    .from('Users')
    .select('*');

  if (error) {
    console.error('Error checking approved user:', error);
    return null;
  }

  const approvedUser = (data || []).find((row) => {
    const rowEmail = String(row.Email || '').trim().toLowerCase();
    return rowEmail === loginEmail;
  });

  if (!approvedUser) {
    await supabase.auth.signOut();
    alert('Your email is not approved to access ProsthetIQ.');
    return null;
  }

  if (!approvedUser.auth_user_id || approvedUser.auth_user_id !== user.id) {
    await supabase
      .from('Users')
      .update({ auth_user_id: user.id })
      .eq('id', approvedUser.id);
  }

  return approvedUser;
}, []);
useEffect(() => {
  let isMounted = true;

  const initializeAuth = async () => {
    try {
      const {
        data: { session: existingSession },
      } = await supabase.auth.getSession();

      if (!isMounted) return;

      const user = existingSession?.user || null;
      setAuthUser(user);

      if (user) {
        const approvedUser = await checkApprovedUser(user);
        if (!isMounted) return;

        setCurrentUserRow(approvedUser);

        if (approvedUser) {
          completePendingAction();
        }
      } else {
        setCurrentUserRow(null);
      }

      if (isMounted) {
        setAuthChecked(true);
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      if (isMounted) {
        setAuthChecked(true);
      }
    }
  };

  initializeAuth();

  const {
  data: { subscription },
} = supabase.auth.onAuthStateChange((_event, newSession) => {
  if (!isMounted) return;

  const user = newSession?.user || null;
  setAuthUser(user);

  if (!user) {
    setCurrentUserRow(null);
    setFavoriteFamilyIds([]);
    setCompareProductIds([]);
  }

  setAuthChecked(true);
});

  return () => {
    isMounted = false;
    subscription.unsubscribe();
  };
}, [checkApprovedUser, completePendingAction]);

const handleSendLoginLink = async () => {
  const email = String(loginEmail || '').trim().toLowerCase();

  if (!email) {
    setLoginMessage('Please enter your email.');
    return;
  }

  setIsSendingLogin(true);
  setLoginMessage('');
  setShowOtpInput(false);

  const { data: approvalData, error: approvalError } =
    await supabase.functions.invoke('ensure-approved-user', {
      body: { email },
    });

  if (approvalError) {
    console.error('Approval check error:', approvalError);
    setLoginMessage('There was a problem checking your account.');
    setIsSendingLogin(false);
    return;
  }

  if (!approvalData?.approved) {
  await supabase
    .from('access_attempts')
    .insert([
      {
        email,
        source: 'login_attempt',
        notes: approvalData?.reason || 'not_approved',
      },
    ]);

  if (approvalData?.reason === 'not_verified') {
    setLoginMessage('Your account request is still pending approval.');
  } else {
    setLoginMessage('No approved account was found for this email.');
  }

  setIsSendingLogin(false);
  return;
}

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: false,
      emailRedirectTo: window.location.origin,
    },
  });

  if (error) {
    console.error(error);
    setLoginMessage('Error sending login code.');
    setShowOtpInput(false);
  } else {
    setLoginMessage('Check your email for your login code.');
    setShowOtpInput(true);
  }

  setIsSendingLogin(false);
};
const handleVerifyOtp = async () => {
  const email = String(loginEmail || '').trim();
  const token = String(otpCode || '').trim();

  if (!email) {
    setLoginMessage('Please enter your email.');
    return;
  }

  if (!token) {
    setLoginMessage('Please enter your login code.');
    return;
  }

  setIsVerifyingOtp(true);
  setLoginMessage('');

  const { error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: 'email',
  });

if (error) {
  console.error(error);
  setLoginMessage('Invalid or expired code.');
} else {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const approvedUser = await checkApprovedUser(user);

  setCurrentUserRow(approvedUser);
  setLoginMessage('Login successful.');
  setOtpCode('');
  setShowOtpInput(false);
  completePendingAction();
}

  setIsVerifyingOtp(false);
};
const handleNewClinicianSignup = async () => {
  const name = signupName.trim();
  const email = signupEmail.trim().toLowerCase();
  const certifyingBody = signupCertifyingBody.trim();
  const certificationNumber = signupCertificationNumber.trim();

  if (!name || !email || !certifyingBody || !certificationNumber) {
    setSignupMessage('Please complete all fields.');
    return;
  }

  setIsSubmittingSignup(true);
  setSignupMessage('');

 const { error } = await supabase
  .from('Users')
  .insert([
    {
      'Name': name,
      'Email': email,
      'Certifying Body': certifyingBody,
      'Certification Number': certificationNumber,
      'role': 'clinician',
      'auth_user_id': null,
      'registration_complete': null,
      'certification_verified': null,
    },
  ]);

  if (error) {
    console.error('Signup request error:', error);
    setSignupMessage('There was a problem submitting your request.');
  } else {
    console.log('Signup request submitted');
    setSignupMessage('Your request has been submitted. Once approved, you will receive a verification email. Please check your inbox, spam, or junk, folder for that email.');
    setSignupName('');
    setSignupEmail('');
    setSignupCertifyingBody('');
    setSignupCertificationNumber('');
  }

  setIsSubmittingSignup(false);
};
const handleLogout = async () => {
  const { error } = await supabase.auth.signOut();

  if (error) {
    console.error('Error signing out:', error);
    alert('Error signing out.');
    return;
  }

  setAuthUser(null);
  setCurrentUserRow(null);
  setAuthChecked(true);
  setFavoriteFamilyIds([]);
  setCompareProductIds([]);
  setPendingAction(null);
  setOtpCode('');
  setShowOtpInput(false);
  localStorage.removeItem('prosthetiq_pending_action');
  goToPage('All Products');
};

useEffect(() => {
  const params = new URLSearchParams(window.location.search);

  const sessionFromUrl = params.get('designSession');

  if (sessionFromUrl) {
    setDesignSessionId(sessionFromUrl);

    console.log(
      'Design Session Detected:',
      sessionFromUrl
    );
  }
}, []);

  useEffect(() => {
    const fetchProducts = async () => {
      const { data, error } = await supabase
  .from('Products')
  .select('*')
  .eq('is_active', true);

      if (error) {
        console.error('Error fetching products:', error);
      } else {
        setProducts(data || []);
      }
    };

    fetchProducts();
  }, []);

 useEffect(() => {
  const fetchJustifications = async () => {
    let allRows = [];
    let from = 0;
    const pageSize = 1000;
    let keepGoing = true;

    while (keepGoing) {
      const { data, error } = await supabase
        .from('Justifications')
        .select('*')
        .range(from, from + pageSize - 1);

      if (error) {
        console.error('Error fetching justifications:', error);
        keepGoing = false;
        break;
      }

      const rows = data || [];
      allRows = [...allRows, ...rows];

      if (rows.length < pageSize) {
        keepGoing = false;
      } else {
        from += pageSize;
      }
    }

    console.log('JUSTIFICATIONS LOADED:', allRows.length);
    console.log(
      'AQUALIMB TF LOADED ROWS:',
      allRows.filter((row) => String(row.product_family_id) === '162')
    );

    setJustifications(allRows);
  };

  fetchJustifications();
}, []);

useEffect(() => {
  const fetchStudies = async () => {
    const { data, error } = await supabase
      .from('studies')
      .select('*');

    if (error) {
      console.error('Error fetching studies:', error);
      setStudies([]);
    } else {
      console.log('STUDIES LOADED:', data?.length || 0);
      setStudies(data || []);
    }
  };

  fetchStudies();
}, []);

useEffect(() => {
  const fetchClinicalBenefitStudies = async () => {
    const { data, error } = await supabase
      .from('clinical_benefit_studies')
      .select('*');

    if (error) {
      console.error('Error fetching clinical benefit studies:', error);
      setClinicalBenefitStudies([]);
    } else {
      console.log(
        'CLINICAL BENEFIT STUDY LINKS LOADED:',
        data?.length || 0
      );
      setClinicalBenefitStudies(data || []);
    }
  };

  fetchClinicalBenefitStudies();
}, []);

  useEffect(() => {
    const fetchLCodeOptions = async () => {
      const { data, error } = await supabase.from('L-Code Options').select('*');

      if (error) {
        console.error('Error fetching L-Code Options:', error);
      } else {
        setLCodeOptionsTable(data || []);
      }
    };

    fetchLCodeOptions();
  }, []);

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!currentUserId) {
        setFavoriteFamilyIds([]);
        return;
      }

      const { data, error } = await supabase
        .from('favorites')
        .select('product_family_id')
        .eq('user_id', currentUserId);

      if (error) {
        console.error('Error fetching favorites:', error);
      } else {
        const ids = (data || [])
          .map((row) => row.product_family_id)
          .filter(Boolean);

        setFavoriteFamilyIds(ids);
      }
    };

    fetchFavorites();
  }, [currentUserId]);

  useEffect(() => {
    const fetchCompare = async () => {
      if (!currentUserId) {
        setCompareProductIds([]);
        return;
      }

      const { data, error } = await supabase
        .from('Compare')
        .select('product_id')
        .eq('user_id', currentUserId);

      if (error) {
        console.error('Error fetching compare list:', error);
      } else {
        const ids = (data || [])
          .map((row) => row.product_id)
          .filter((id) => id !== null && id !== undefined);

        setCompareProductIds(ids);
      }
    };

    fetchCompare();
  }, [currentUserId]);

  useEffect(() => {
    const fetchClinicalBenefits = async () => {
      if (!selectedJustification) {
        setClinicalBenefits([]);
        setExpandedBenefit(null);
        return;
      }

            const selectedCodes = String(selectedJustification['L-CODE'] || '')
        .split(/\s+OR\s+|[,;/]+/i)
        .map((code) => normalizeCode(code))
        .filter(Boolean);

      if (selectedCodes.length === 0) {
        setClinicalBenefits([]);
        setExpandedBenefit(null);
        return;
      }

      const { data, error } = await supabase
        .from('Clinical Benefits Library')
        .select('*');

      if (error) {
        console.error('Error fetching clinical benefits:', error);
        setClinicalBenefits([]);
        return;
      }

      const matchingBenefits = (data || []).filter((row) => {
        const rowCodes = String(row['L-CODES'] || '')
          .split(/\s+OR\s+|[,;/]+/i)
          .map((code) => normalizeCode(code))
          .filter(Boolean);

        const rowComponentCategory = normalizeText(row['Component Category']);

let productComponentCategory = normalizeText(
  selectedVariant?.['DEVICE TYPE'] ||
  selectedFamily?.['DEVICE TYPE'] ||
  ''
);

if (productComponentCategory === 'INTEGRATED SYSTEM') {
  productComponentCategory = 'KNEE';
}

const codeMatches = rowCodes.some((code) => selectedCodes.includes(code));

const categoryMatches =
  !rowComponentCategory ||
  rowComponentCategory === productComponentCategory;

return codeMatches && categoryMatches;
      });

      setClinicalBenefits(matchingBenefits);
      setExpandedBenefit(null);
    };

    fetchClinicalBenefits();
  }, [selectedJustification, selectedVariant, selectedFamily]);

  useEffect(() => {
    const fetchFeedbackData = async () => {
      if (!selectedVariant?.id) {
        setProductRatings([]);
        setProductComments([]);
        setCommentUsers({});
        setSelectedFeedback('');
        setCommentInput('');
        setSubmitAnonymous(false);
        setShowNegativeModal(false);
        return;
      }

      const [ratingsResponse, commentsResponse] = await Promise.all([
        supabase
          .from('ratings')
          .select('*')
          .eq('product_id', selectedVariant.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('comments')
          .select('*')
          .eq('product_id', selectedVariant.id)
          .order('created_at', { ascending: false }),
      ]);

      if (ratingsResponse.error) {
        console.error('Error fetching ratings:', ratingsResponse.error);
        setProductRatings([]);
      } else {
        setProductRatings(ratingsResponse.data || []);
      }

      if (commentsResponse.error) {
        console.error('Error fetching comments:', commentsResponse.error);
        setProductComments([]);
        setCommentUsers({});
      } else {
        const comments = commentsResponse.data || [];
        setProductComments(comments);

        const userIds = [
          ...new Set(
            comments
              .map((comment) => comment.user_id)
              .filter((id) => id !== null && id !== undefined)
          ),
        ];

        if (userIds.length > 0) {
          const { data: usersData, error: usersError } = await supabase
            .from('Users')
            .select('*')
            .in('id', userIds);

          if (usersError) {
            console.error('Error fetching comment users:', usersError);
            setCommentUsers({});
          } else {
            const userMap = {};
            (usersData || []).forEach((user) => {
              userMap[user.id] = getUserDisplayName(user);
            });
            setCommentUsers(userMap);
          }
        } else {
          setCommentUsers({});
        }
      }

      setSelectedFeedback('');
      setCommentInput('');
      setSubmitAnonymous(false);
      setShowNegativeModal(false);
    };

    fetchFeedbackData();
  }, [selectedVariant]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedFamily(null);
    setSelectedVariant(null);
    setSelectedJustification(null);
    setClinicalBenefits([]);
    setExpandedBenefit(null);
    setProductRatings([]);
    setProductComments([]);
    setCommentUsers({});
    setSelectedFeedback('');
    setCommentInput('');
    setSubmitAnonymous(false);
    setShowNegativeModal(false);
  }, [activePage, searchTerm, clinicalFilter, lCodeFilter, manufacturerFilter]);
const kyanResults = useMemo(() => {
  const prompt = String(kyanPrompt || '').trim();

  if (!prompt) return [];

  const detectedDeviceType = detectDeviceType(prompt);
  const detectedKLevel = detectFunctionalLevel(prompt);
  const detectedAmpLevel = detectAmpLevel(prompt);
  const detectedWeight = extractWeightFromPrompt(prompt);
  const promptText = normalizeText(prompt);
  
  let detectedIntent = detectClinicalIntent(prompt);

  let kneeIntents = [];

if (detectedDeviceType === 'KNEE') {
  if (
    promptText.includes('UNSPECIFIED') ||
    promptText.includes('OTHER') ||
    promptText.includes('L5999')
  ) {
    kneeIntents = ['knee_unspecified'];
  } else if (
    promptText.includes('MOTOR') ||
    promptText.includes('POWERED') ||
    promptText.includes('MICROPROCESSOR') ||
    promptText.includes('ADVANCED CONTROL') ||
    promptText.includes('L5827') ||
    promptText.includes('L5856') ||
    promptText.includes('L5859')
  ) {
    kneeIntents = ['variable_cadence', 'gait_efficiency', 'motor_control'];
  } else if (detectedKLevel === 'K3' || detectedKLevel === 'K4') {
    kneeIntents = ['variable_cadence', 'gait_efficiency', 'motor_control'];
  } else if (detectedKLevel === 'K2') {
    kneeIntents = ['gait_efficiency', 'variable_cadence'];
  } else if (detectedKLevel === 'K1') {
    kneeIntents = ['maximum_stability'];
  } else {
    kneeIntents = ['gait_efficiency', 'variable_cadence', 'motor_control'];
  }

  detectedIntent = kneeIntents[0];
}
  const defaultBaseCodes = (() => {
  if (detectedIntent !== 'foot_stability') return [];

  if (detectedKLevel === 'K1') {
    return ['L5970', 'L5971', 'L5974'];
  }

  if (detectedKLevel === 'K2') {
    return ['L5972'];
  }

  return [];
})();

  let selectedGroup = null;
let activeKyanGroups = FOOT_KYAN_GROUPS;

if (detectedDeviceType === 'KNEE') {
  activeKyanGroups = KNEE_KYAN_GROUPS;
}

if (detectedDeviceType === 'HIP') {
  activeKyanGroups = HIP_KYAN_GROUPS;
}

selectedGroup = activeKyanGroups[detectedIntent];

if (detectedDeviceType === 'KNEE' && !selectedGroup) {
  selectedGroup = KNEE_KYAN_GROUPS.gait_efficiency;
}

if (detectedDeviceType === 'HIP' && !selectedGroup) {
  selectedGroup = HIP_KYAN_GROUPS.hip_stability;
}

if (!selectedGroup) return [];

   const groupProducts = (group) => {
     const activeCodes =
       defaultBaseCodes.length > 0 ? defaultBaseCodes : group.codes;
 
     const groupMatches = [...products]
      .filter((p) => {
         const codes = getVariantLCodes(p);
         return activeCodes.some((code) => codes.includes(code));
     })
    .filter((p) => {
  if (detectedDeviceType) {
    const productDeviceType = normalizeText(p['DEVICE TYPE']);

    if (detectedDeviceType === 'KNEE') {
      return (
        productDeviceType === 'KNEE' ||
        productDeviceType === 'INTEGRATED SYSTEM'
      );
    }

    return productDeviceType === detectedDeviceType;
  }

  return true;
})
    .filter((p) => {
  if (detectedDeviceType === 'KNEE') {
  return true;
 }	

  if (detectedKLevel) {
    return normalizeText(p['MANUFACTURER K LEVEL']).includes(detectedKLevel);
  }

  return true;
})
    .filter((p) => {
      if (detectedAmpLevel && detectedIntent !== 'shock_absorption') {
        return normalizeText(p['AMP_LEVEL_SEARCH']).includes(detectedAmpLevel);
      }
      return true;
    })
    .filter((p) => {
      if (detectedWeight) {
        const max = Number(getMaxUserWeightLbs(p));
        return !isNaN(max) && max >= detectedWeight;
      }
      return true;
    });

  return Object.values(
    groupMatches.reduce((acc, product) => {
      const key =
        product.product_family_id ||
        product['product_family_id'] ||
        product['family_name'] ||
        product['PRODUCT NAME'];

      if (!acc[key]) {
        acc[key] = {
          ...product,
          matchingVariants: [product],
        };
      } else {
        acc[key].matchingVariants.push(product);
      }

      return acc;
    }, {})
  );
};

if (detectedDeviceType === 'KNEE' && kneeIntents.length > 0) {
  return kneeIntents
    .map((intent) => KNEE_KYAN_GROUPS[intent])
    .filter(Boolean)
    .map((group) => ({
  title: group.title,
  associatedCodes: group.associatedCodes,
  description: group.description,
  refinements: group.refinements,
  defaultBaseCodes: [],
  products: groupProducts(group),
}));
}

return [
  {
    title: selectedGroup.title,
    associatedCodes: selectedGroup.associatedCodes,
    description: selectedGroup.description,
    refinements: selectedGroup.refinements,
    defaultBaseCodes,
    products: groupProducts(selectedGroup),
  },
];
}, [kyanPrompt, products]);

  const manufacturers = useMemo(() => {
    const values = products
      .filter((product) => pageMatchesDeviceType(activePage, product['DEVICE TYPE']))
      .map((product) => product['MANUFACTURER'])
      .filter(Boolean);

    return [...new Set(values)].sort((a, b) => a.localeCompare(b));
  }, [products, activePage]);

  const lCodes = useMemo(() => {
    const tableCodes = (lCodeOptionsTable || [])
      .filter((row) => {
        const deviceType = getLCodeOptionDeviceType(row);

        if (activePage === 'All Products' || activePage === 'Favorites') return true;
        if (activePage === 'Kyan') return true;
        if (activePage === 'Compare List') return true;
        if (activePage === 'Login') return true;
        if (!deviceType) return true;

        return pageMatchesDeviceType(activePage, deviceType);
      })
      .map((row) => getLCodeOptionValue(row))
      .filter(Boolean);

    const uniqueTableCodes = [...new Set(tableCodes)].sort((a, b) =>
      a.localeCompare(b)
    );

    if (uniqueTableCodes.length > 0) {
      return uniqueTableCodes;
    }

    const fallbackProductCodes = new Set();

    products
      .filter((product) => pageMatchesDeviceType(activePage, product['DEVICE TYPE']))
      .forEach((product) => {
        const value =
          product['L-CODE'] ||
          product['L-CODES'] ||
          product['L CODE'] ||
          product['L CODES'] ||
          product['HCPCS CODES'];

        if (typeof value === 'string') {
          value
            .split(/[,;/]+/)
            .map((code) => code.trim())
            .filter(Boolean)
            .forEach((code) => fallbackProductCodes.add(code));
        }
      });

    return [...fallbackProductCodes].sort((a, b) => a.localeCompare(b));
  }, [lCodeOptionsTable, products, activePage]);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    if (activePage === 'Feet') {
      filtered = filtered.filter((product) => product['DEVICE TYPE'] === 'FOOT');
    }

    if (activePage === 'Knees') {
      filtered = filtered.filter((product) => product['DEVICE TYPE'] === 'KNEE');
    }

    if (activePage === 'Hips') {
      filtered = filtered.filter((product) => product['DEVICE TYPE'] === 'HIP');
    }

    if (activePage === 'Integrated Systems') {
      filtered = filtered.filter(
        (product) => product['DEVICE TYPE'] === 'INTEGRATED SYSTEM'
      );
    }

    if (activePage === 'Favorites') {
      filtered = filtered.filter((product) =>
        favoriteFamilyIds.includes(product.product_family_id)
      );
    }

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();

      filtered = filtered.filter((product) => {
        const searchableText = [
          product.name,
          product['PRODUCT NAME'],
          product['family_name'],
          product['MANUFACTURER'],
          product['DEVICE TYPE'],
          product['L-CODE'],
          product['L-CODES'],
          product['L CODE'],
          product['L CODES'],
          product['HCPCS CODES'],
          product['PRODUCT DESCRIPTION'],
          product['DESCRIPTION'],
          product['PDAC VERIFIED'],
          product['WATER CAPABILITY'],
          product['SPECIAL USE'],
          product['ADULT OR PEDIATRIC'],
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchableText.includes(query);
      });
    }

    if (clinicalFilter !== 'all') {
      filtered = filtered.filter((product) => {
        const adultOrPediatric = normalizeText(product['ADULT OR PEDIATRIC']);
        const pdacVerified = normalizeText(product['PDAC VERIFIED']);
        const waterCategory = getWaterCategory(product['WATER CAPABILITY']);
        const specialUse = normalizeText(product['SPECIAL USE']);

        if (clinicalFilter === 'adult') return adultOrPediatric === 'ADULT';
        if (clinicalFilter === 'pediatric') return adultOrPediatric === 'PEDIATRIC';

        if (clinicalFilter === 'pdac_verified') {
          return (
            pdacVerified === 'YES' ||
            pdacVerified === 'TRUE' ||
            pdacVerified === 'PDAC VERIFIED'
          );
        }

        if (clinicalFilter === 'special_use') {
          return (
            specialUse &&
            specialUse !== 'NO' &&
            specialUse !== 'N/A' &&
            specialUse !== 'NA' &&
            specialUse !== 'NONE' &&
            specialUse !== 'NULL'
          );
        }

        if (
          clinicalFilter === 'suitable_outdoor' ||
          clinicalFilter === 'water_resistant' ||
          clinicalFilter === 'weatherproof' ||
          clinicalFilter === 'waterproof'
        ) {
          return waterCategory === clinicalFilter;
        }

        return true;
      });
    }

    if (lCodeFilter !== 'all') {
      filtered = filtered.filter((product) => {
        const rawValue =
          product['L-CODE'] ||
          product['L-CODES'] ||
          product['L CODE'] ||
          product['L CODES'] ||
          product['HCPCS CODES'] ||
          '';

        const rawText = String(rawValue).trim();
        const normalizedSelected = normalizeCode(lCodeFilter);

        if (rawText.includes(lCodeFilter)) return true;

        const splitCodes = rawText
          .split(/\s+OR\s+|[,;/]+/i)
          .map((code) => normalizeCode(code))
          .filter(Boolean);

        return splitCodes.includes(normalizedSelected);
      });
    }

    if (patientWeight.trim()) {
      const enteredWeight = Number(patientWeight);

      if (!Number.isNaN(enteredWeight) && enteredWeight > 0) {
        filtered = filtered.filter((product) => {
          const maxWeight = Number(getMaxUserWeightLbs(product));
          return !Number.isNaN(maxWeight) && maxWeight >= enteredWeight;
        });
      }
    }

    if (manufacturerFilter !== 'all') {
      filtered = filtered.filter(
        (product) => product['MANUFACTURER'] === manufacturerFilter
      );
    }

    return filtered;
  }, [
    products,
    activePage,
    searchTerm,
    clinicalFilter,
    lCodeFilter,
    patientWeight,
    manufacturerFilter,
    favoriteFamilyIds,
  ]);

  const familyProducts = useMemo(() => {
    const groupedMap = new Map();

    filteredProducts.forEach((product) => {
      const familyName =
        product['family_name'] ||
        product.name ||
        product['PRODUCT NAME'] ||
        'Unnamed Product';

      const manufacturer = product['MANUFACTURER'] || '';
      const deviceType = product['DEVICE TYPE'] || '';
      const familyId = product['product_family_id'] || familyName;
      const key = `${familyId}__${manufacturer}__${deviceType}__${familyName}`;

      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          ...product,
          familyKey: key,
          familyId,
          display_name: familyName,
          variants: [product],
        });
      } else {
        const existing = groupedMap.get(key);
        existing.variants.push(product);

        if (!existing['PRODUCT IMAGE'] && product['PRODUCT IMAGE']) {
          existing['PRODUCT IMAGE'] = product['PRODUCT IMAGE'];
        }
      }
    });

    const grouped = Array.from(groupedMap.values());

    grouped.sort((a, b) => {
      const manufacturerCompare = String(a['MANUFACTURER'] || '').localeCompare(
        String(b['MANUFACTURER'] || '')
      );

      if (manufacturerCompare !== 0) return manufacturerCompare;

      return String(a.display_name || '').localeCompare(
        String(b.display_name || '')
      );
    });

    return grouped;
  }, [filteredProducts]);

  const compareProducts = useMemo(() => {
    return compareProductIds
      .map((id) => products.find((product) => product.id === id))
      .filter(Boolean);
  }, [compareProductIds, products]);

  useEffect(() => {
    const ids = compareProducts.map((product) => product.id);

    if (ids.length === 0) {
      setActiveCompareLeftId(null);
      setActiveCompareRightId(null);
      return;
    }

    setActiveCompareLeftId((prev) => {
      if (prev === null) return null;
      if (ids.includes(prev)) return prev;
      return ids[0] || null;
    });

    setActiveCompareRightId((prev) => {
      if (prev === null) return null;
      if (ids.includes(prev) && prev !== activeCompareLeftId) return prev;

      const fallback = ids.find((id) => id !== activeCompareLeftId);
      return fallback || null;
    });
  }, [compareProducts, activeCompareLeftId]);

  const activeCompareLeft = useMemo(
    () => compareProducts.find((product) => product.id === activeCompareLeftId) || null,
    [compareProducts, activeCompareLeftId]
  );

  const activeCompareRight = useMemo(
    () => compareProducts.find((product) => product.id === activeCompareRightId) || null,
    [compareProducts, activeCompareRightId]
  );

  const totalPages = Math.ceil(familyProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = familyProducts.slice(startIndex, endIndex);

  const feedbackSummary = useMemo(() => {
    const counts = {
      likely: 0,
      consider: 0,
      not_likely: 0,
    };

    productRatings.forEach((row) => {
      const value = row.feedback;
      if (counts[value] !== undefined) {
        counts[value] += 1;
      }
    });

    const total = counts.likely + counts.consider + counts.not_likely;

    const getPercent = (count) => {
      if (!total) return 0;
      return Math.round((count / total) * 100);
    };

    return {
      total,
      counts,
      percentages: {
        likely: getPercent(counts.likely),
        consider: getPercent(counts.consider),
        not_likely: getPercent(counts.not_likely),
      },
    };
  }, [productRatings]);

  const visibleComments = useMemo(() => {
    return productComments
      .map((comment) => {
        const parsed = parseAnonymousComment(comment.comment_text);

        return {
          ...comment,
          parsedText: parsed.text,
          isAnonymous: parsed.isAnonymous,
        };
      })
      .filter((comment) => comment.parsedText);
  }, [productComments]);
const handleToggleFavorite = useCallback(
  async (product, overrideUserId = null) => {
    const actingUserId = overrideUserId || currentUserId;
    const familyId = product?.product_family_id;

    console.log('FAVORITE CLICKED', {
      actingUserId,
      familyId,
      productId: product?.id,
      productName: product?.['PRODUCT NAME'],
    });

    if (!actingUserId) {
      console.log('No actingUserId, sending to login');
      goToLoginPage({ type: 'favorite', product });
      return;
    }

    if (!familyId) {
      console.error('Product is missing product_family_id, cannot favorite.');
      alert(
        'This product is missing product_family_id, so it cannot be added to favorites yet.'
      );
      return;
    }

    const isFavorited = favoriteFamilyIds.includes(familyId);
    console.log('isFavorited?', isFavorited);

    if (isFavorited) {
      const previousIds = [...favoriteFamilyIds];
      setFavoriteFamilyIds((prev) => prev.filter((id) => id !== familyId));

      const { data, error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', actingUserId)
        .eq('product_family_id', familyId)
        .select();

      console.log('DELETE favorite result', { data, error });

      if (error) {
        console.error('Error removing favorite:', error);
        alert(`Error removing favorite: ${error.message}`);
        setFavoriteFamilyIds(previousIds);
        return;
      }

      const { error: eventError } = await supabase.from('favorite_events').insert([
        {
          user_id: actingUserId,
          product_family_id: familyId,
          event_type: 'removed',
        },
      ]);

      if (eventError) {
        console.error('Error logging favorite removal event:', eventError);
      }
    } else {
      const previousIds = [...favoriteFamilyIds];
      setFavoriteFamilyIds((prev) => [...prev, familyId]);

      const { data, error } = await supabase
        .from('favorites')
        .insert([
          {
            user_id: actingUserId,
            product_family_id: familyId,
          },
        ])
        .select();

      console.log('INSERT favorite result', { data, error });

      if (error) {
        console.error('Error adding favorite:', error);
        alert(`Error adding favorite: ${error.message}`);
        setFavoriteFamilyIds(previousIds);
        return;
      }

      const { error: eventError } = await supabase.from('favorite_events').insert([
        {
          user_id: actingUserId,
          product_family_id: familyId,
          event_type: 'added',
        },
      ]);

      if (eventError) {
        console.error('Error logging favorite add event:', eventError);
      }
    }
  },
  [currentUserId, favoriteFamilyIds, goToLoginPage]
);
  const toggleFavorite = async (e, product) => {
    e.stopPropagation();
    await handleToggleFavorite(product);
  };

 const handleToggleCompare = useCallback(
  async (product, overrideUserId = null) => {
    const actingUserId = overrideUserId || currentUserId;
    const productId = product?.id;

    console.log('COMPARE CLICKED', {
      actingUserId,
      productId,
      productName: product?.['PRODUCT NAME'],
    });

    if (!actingUserId) {
      console.log('No actingUserId, sending to login');
      goToLoginPage({ type: 'compare', product });
      return;
    }

    if (!productId) {
      console.error('Product is missing id, cannot add to compare.');
      alert(
        'This product is missing its product id, so it cannot be added to compare yet.'
      );
      return;
    }

    const isInCompare = compareProductIds.includes(productId);
    console.log('isInCompare?', isInCompare);

    if (isInCompare) {
      const previousIds = [...compareProductIds];
      setCompareProductIds((prev) => prev.filter((id) => id !== productId));

      const { data, error } = await supabase
        .from('Compare')
        .delete()
        .eq('user_id', actingUserId)
        .eq('product_id', productId)
        .select();

      console.log('DELETE compare result', { data, error });

      if (error) {
        console.error('Error removing compare item:', error);
        alert(`Error removing compare item: ${error.message}`);
        setCompareProductIds(previousIds);
        return;
      }

      const { error: eventError } = await supabase.from('compare_events').insert([
        {
          user_id: actingUserId,
          product_id: productId,
          event_type: 'removed',
        },
      ]);

      if (eventError) {
        console.error('Error logging compare removal event:', eventError);
      }

      if (activeCompareLeftId === productId) setActiveCompareLeftId(null);
      if (activeCompareRightId === productId) setActiveCompareRightId(null);

      return;
    }

    if (compareProductIds.length >= 3) {
      alert('You can keep up to 3 products in Compare.');
      return;
    }

    const previousIds = [...compareProductIds];
    setCompareProductIds((prev) => [...prev, productId]);

    const { data, error } = await supabase
      .from('Compare')
      .insert([
        {
          user_id: actingUserId,
          product_id: productId,
        },
      ])
      .select();

    console.log('INSERT compare result', { data, error });

    if (error) {
      console.error('Error adding compare item:', error);
      alert(`Error adding compare item: ${error.message}`);
      setCompareProductIds(previousIds);
      return;
    }

    const { error: eventError } = await supabase.from('compare_events').insert([
      {
        user_id: actingUserId,
        product_id: productId,
        event_type: 'added',
      },
    ]);

    if (eventError) {
      console.error('Error logging compare add event:', eventError);
    }
  },
  [currentUserId, compareProductIds, activeCompareLeftId, activeCompareRightId, goToLoginPage]
);

  const toggleCompare = async (e, product) => {
    e.stopPropagation();
    await handleToggleCompare(product);
  };

const addProductToDesign = async (
  product,
  benefit,
  lCode
) => {
  if (!designSessionId) {
    alert('No active design session detected.');
    return;
  }

  if (!authUser?.id) {
  goToLoginPage({ type: 'page', page: 'All Products' });
  return;
}

  const selectedBenefits = [
    {
      code: lCode || '',
      category:
        benefit['Benefit Category'] ||
        'Unnamed Benefit',
      clinical_benefit_statement:
        benefit['Clinical Benefit Statement'] ||
        '',
      functional_problem:
        benefit['Functional Problem Addressed'] ||
        '',
      patient_scenario:
        benefit['Typical Patient Scenario'] ||
        '',
    },
  ];

  const designSelectionRow = {
    user_id: authUser.id,
    session_id: designSessionId,
    product_id: product.id,
    product_name:
      product['PRODUCT NAME'] ||
      product.name ||
      'Unnamed Product',
    category:
      product['DEVICE TYPE'] ||
      'Unknown',
    selected_benefits: selectedBenefits,
    l_code: String(lCode || ''),
  };

  const { error } = await supabase
    .from('design_component_selections')
    .insert([designSelectionRow]);

  if (error) {
    console.error(
      'Error adding product to design:',
      error
    );

    alert('Error adding product to design.');
    return;
  }

  const { error: eventError } = await supabase
    .from('design_selection_events')
    .insert([
      {
        ...designSelectionRow,
        action_type: 'add_to_design',
      },
    ]);

  if (eventError) {
    console.error(
      'Error logging design selection event:',
      eventError
    );
  }

  console.log(
    'Product added to design:',
    designSelectionRow
  );

  alert('Product added to ProsthetIQ Design.');
};

const setCompareLeft = (productId) => {
  setActiveCompareLeftId(productId);

  if (activeCompareRightId === productId) {
    const fallback = compareProducts.find((product) => product.id !== productId);
    setActiveCompareRightId(fallback?.id || null);
  }
};

  const setCompareRight = (productId) => {
    setActiveCompareRightId(productId);

    if (activeCompareLeftId === productId) {
      const fallback = compareProducts.find((product) => product.id !== productId);
      setActiveCompareLeftId(fallback?.id || null);
    }
  };

  const clearCompareLeft = () => {
    setActiveCompareLeftId(null);
  };

  const clearCompareRight = () => {
    setActiveCompareRightId(null);
  };

  const openFamily = (family) => {
    setSelectedFamily(family);
    setSelectedVariant(null);
    setSelectedJustification(null);
    setClinicalBenefits([]);
    setExpandedBenefit(null);
  };

  const openVariant = (variant) => {
    setSelectedVariant(variant);
    setSelectedJustification(null);
    setClinicalBenefits([]);
    setExpandedBenefit(null);
  };

  const closeFamily = () => {
    setSelectedFamily(null);
    setSelectedVariant(null);
    setSelectedJustification(null);
    setClinicalBenefits([]);
    setExpandedBenefit(null);
  };

  const closeVariant = () => {
    setSelectedVariant(null);
    setSelectedJustification(null);
    setClinicalBenefits([]);
    setExpandedBenefit(null);
    setProductRatings([]);
    setProductComments([]);
    setCommentUsers({});
    setSelectedFeedback('');
    setCommentInput('');
    setSubmitAnonymous(false);
    setShowNegativeModal(false);
  };

  const openJustification = (justification) => {
    setSelectedJustification(justification);
    setExpandedBenefit(null);
  };

  const closeJustification = () => {
    setSelectedJustification(null);
    setClinicalBenefits([]);
    setExpandedBenefit(null);
  };
  const clearKyan = () => {
  setKyanPrompt('');
  setExpandedKyanGroup(null);
  setKyanRefinements({});
  setKyanBaseCodeFilters({});
};

    const matchingJustifications = useMemo(() => {
  if (!selectedFamily?.product_family_id) return [];

  const matches = justifications
    .filter((row) => {
      return String(row.product_family_id) === String(selectedFamily.product_family_id);
    })
    .sort((a, b) =>
      String(a['L-CODE'] || '').localeCompare(String(b['L-CODE'] || ''))
    );

  const uniqueByCode = new Map();

  matches.forEach((row) => {
    const code = normalizeCode(row['L-CODE']);

    if (code && !uniqueByCode.has(code)) {
      uniqueByCode.set(code, row);
    }
  });

  return Array.from(uniqueByCode.values());
}, [selectedFamily?.product_family_id, justifications]);
  const pageTitle = activePage === 'Kyan' ? 'Kyan' : activePage;

  const refreshFeedback = useCallback(async () => {
    if (!selectedVariant?.id) return;

    const [ratingsResponse, commentsResponse] = await Promise.all([
      supabase
        .from('ratings')
        .select('*')
        .eq('product_id', selectedVariant.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('comments')
        .select('*')
        .eq('product_id', selectedVariant.id)
        .order('created_at', { ascending: false }),
    ]);

    if (ratingsResponse.error) {
      console.error('Error refreshing ratings:', ratingsResponse.error);
    } else {
      setProductRatings(ratingsResponse.data || []);
    }

    if (commentsResponse.error) {
      console.error('Error refreshing comments:', commentsResponse.error);
    } else {
      const comments = commentsResponse.data || [];
      setProductComments(comments);

      const userIds = [
        ...new Set(
          comments
            .map((comment) => comment.user_id)
            .filter((id) => id !== null && id !== undefined)
        ),
      ];

      if (userIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('Users')
          .select('*')
          .in('id', userIds);

        if (usersError) {
          console.error('Error refreshing comment users:', usersError);
        } else {
          const userMap = {};
          (usersData || []).forEach((user) => {
            userMap[user.id] = getUserDisplayName(user);
          });
          setCommentUsers(userMap);
        }
      } else {
        setCommentUsers({});
      }
    }
  }, [selectedVariant]);

  const submitFeedback = async () => {
    if (!selectedVariant?.id) return;

    if (!currentUserId) {
      goToLoginPage({ type: 'feedback' });
      return;
    }

    const cleanedComment = String(commentInput || '').trim();

    if (!selectedFeedback && !cleanedComment) {
      alert('Please choose feedback, write a comment, or both.');
      return;
    }

    setIsSubmittingFeedback(true);

    try {
      if (selectedFeedback) {
        const { error: ratingError } = await supabase.from('ratings').insert([
          {
            user_id: currentUserId,
            product_id: selectedVariant.id,
            feedback: selectedFeedback,
          },
        ]);

        if (ratingError) throw ratingError;
      }

      if (cleanedComment) {
        const storedComment = buildStoredComment(cleanedComment, submitAnonymous);

        const { error: commentError } = await supabase.from('comments').insert([
          {
            user_id: currentUserId,
            product_id: selectedVariant.id,
            comment_text: storedComment,
          },
        ]);

        if (commentError) throw commentError;
      }

      await refreshFeedback();

      setSelectedFeedback('');
      setCommentInput('');
      setSubmitAnonymous(false);
      setShowNegativeModal(false);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert(`Error submitting feedback: ${error.message}`);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleSubmitClick = () => {
    if (!isLoggedIn) {
      goToLoginPage({ type: 'feedback' });
      return;
    }

    if (selectedFeedback === 'not_likely') {
      setShowNegativeModal(true);
      return;
    }

    submitFeedback();
  };

  const renderLoginPage = () => {
    const cardStyle = {
      maxWidth: '560px',
      margin: '32px auto',
      padding: '28px',
      borderRadius: '18px',
      border: '1px solid #ddd',
      background: darkMode ? '#121212' : '#ffffff',
      boxShadow: darkMode
        ? '0 2px 14px rgba(0,0,0,0.35)'
        : '0 2px 12px rgba(0,0,0,0.06)',
    };

    const inputStyle = {
      width: '100%',
      padding: '12px 14px',
      borderRadius: '12px',
      border: '1px solid #ccc',
      fontSize: '15px',
      marginBottom: '14px',
      boxSizing: 'border-box',
    };

    const buttonStyle = {
      padding: '12px 16px',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: 700,
      fontSize: '15px',
    };

    return (
      <section style={{ paddingTop: '12px' }}>
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0, marginBottom: '8px' }}>
            Sign in to unlock ProsthetIQ features
          </h2>

          <p style={{ marginTop: 0, marginBottom: '18px', lineHeight: 1.5 }}>
            Browse products freely, then sign in to:
          </p>

          <div style={{ marginBottom: '18px', lineHeight: 1.7 }}>
            <div>⭐ Save favorites</div>
            <div>⚖️ Compare components</div>
            <div>💬 Leave feedback & ratings</div>
            <div>🧠 Use Kyan AI </div>
          </div>

          <input
            type="email"
            placeholder="Enter your email"
            value={loginEmail}
            onChange={(e) => {
            setLoginEmail(e.target.value);
            setLoginMessage('');
            setOtpCode('');
            setShowOtpInput(false);
          }}
            style={inputStyle}
          />

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={handleSendLoginLink}
              disabled={isSendingLogin}
              style={buttonStyle}
            >
              {isSendingLogin ? 'Sending code...' : 'Send login code'}
            </button>

            <button
              onClick={() => goToPage('All Products')}
              style={{
                ...buttonStyle,
                background: 'transparent',
                border: '1px solid #ccc',
              }}
            >
              Continue browsing
            </button>
          </div>

          {loginMessage && (
            <p style={{ marginTop: '16px', marginBottom: 0 }}>{loginMessage}</p>
          )}
       {showOtpInput && (
        <>
         <input
          type="text"
          placeholder="Enter login code"
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value)}
          maxLength={10}
          style={inputStyle}
        />

         <button
          onClick={handleVerifyOtp}
          disabled={isVerifyingOtp}
          style={buttonStyle}
       >
      {isVerifyingOtp ? 'Verifying...' : 'Verify code'}
    </button>
  </>
)}

          <p style={{ marginTop: '18px', opacity: 0.75, fontSize: '13px' }}>
            Approved users can sign in to access ProsthetIQ features.
          </p>
<div style={{ marginTop: '12px' }}>
  {!showSignupForm ? (
    <button
      onClick={() => {
        setShowSignupForm(true);
        setSignupMessage('');
      }}
      style={{
        background: 'none',
        border: 'none',
        color: '#4f8cff',
        cursor: 'pointer',
        fontWeight: 600,
        padding: 0,
      }}
    >
      New clinician? Request access
    </button>
  ) : (
    <div style={{ marginTop: '12px', display: 'grid', gap: '10px' }}>
      <input
        type="text"
        placeholder="Full name"
        value={signupName}
        onChange={(e) => setSignupName(e.target.value)}
      />

      <input
        type="email"
        placeholder="Email"
        value={signupEmail}
        onChange={(e) => setSignupEmail(e.target.value)}
      />

      <input
        type="text"
        placeholder="Certifying body"
        value={signupCertifyingBody}
        onChange={(e) => setSignupCertifyingBody(e.target.value)}
      />

      <input
        type="text"
        placeholder="Certification number"
        value={signupCertificationNumber}
        onChange={(e) => setSignupCertificationNumber(e.target.value)}
      />

      <button
        onClick={handleNewClinicianSignup}
        disabled={isSubmittingSignup}
      >
        {isSubmittingSignup ? 'Submitting...' : 'Submit request'}
      </button>

      <button
        onClick={() => {
          setShowSignupForm(false);
          setSignupMessage('');
        }}
      >
        Cancel
      </button>

      {signupMessage && <p>{signupMessage}</p>}
    </div>
  )}
</div>
        </div>
      </section>
    );
  };

  const renderJustificationDetails = () => {
    if (!selectedJustification) return null;

    const item = selectedJustification;
    const instructionsUrl = formatUrl(item['Instructions for Use']);
    const supportingDoc1 = formatUrl(item['Supporting Documents 1']);
    const supportingDoc2 = formatUrl(item['Supporting Documents 2']);

    return (
      <section className="details-section">
        <button className="back-button" onClick={closeJustification}>
          ← Back to Product Details
        </button>

        <div className="detail-card">
          <h2>Code: {item['L-CODE'] || 'N/A'}</h2>

	<p>
  	  <strong>Description:</strong>
  	  <br />
  	  {item['Description'] || 'No description available.'}
	</p>

          <p>
  	    <strong>Clinician Justification:</strong>
  	    <br />
  	    {item['Justification'] || 'No justification available.'}
	  </p>

          <p>
            <strong>Instructions for Use:</strong>
            <br />
            {instructionsUrl ? (
              <a href={instructionsUrl} target="_blank" rel="noreferrer">
                Open Instruction Manual
              </a>
            ) : (
              'No instructions listed.'
            )}
          </p>

          {supportingDoc1 && (
            <p>
              <strong>Supporting Document 1:</strong>{' '}
              <a href={supportingDoc1} target="_blank" rel="noreferrer">
                Open Document
              </a>
            </p>
          )}

          {supportingDoc2 && (
            <p>
              <strong>Supporting Document 2:</strong>{' '}
              <a href={supportingDoc2} target="_blank" rel="noreferrer">
                Open Document
              </a>
            </p>
          )}

          <div style={{ marginTop: '24px' }}>
            <h3>Clinical Benefits</h3>

            {clinicalBenefits.length === 0 ? (
              <p>No clinical benefits found for this L-code.</p>
            ) : (
              <div>
                {clinicalBenefits.map((benefit, index) => {
  			const isExpanded = expandedBenefit === index;

  			const linkedStudyIds = clinicalBenefitStudies
  .filter(
    (link) =>
      String(link.benefit_row_id) === String(benefit.benefit_row_id)
  )
  .map((link) => String(link.study_id));

if (linkedStudyIds.length === 0 && benefit.study_id) {
  linkedStudyIds.push(String(benefit.study_id));
}

const supportingStudies = studies.filter((study) =>
  linkedStudyIds.includes(String(study.study_id))
);

  			const isEvidenceExpanded = expandedEvidence === index;

  		return (
  <div
    key={
      benefit.id ||
      benefit['Benefit ID'] ||
      `${benefit['L-CODES']}-${benefit['Benefit Category']}-${index}`
    }
    style={{
      borderTop: '1px solid #ddd',
      paddingTop: '12px',
      paddingBottom: '12px',
    }}
  >
    <button
      onClick={() =>
        setExpandedBenefit(isExpanded ? null : index)
      }
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        margin: 0,
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        textAlign: 'left',
        color: 'inherit',
      }}
    >
      {benefit['Benefit Category'] || 'Unnamed Benefit'}
    </button>

    {isExpanded && (
      <div style={{ marginTop: '10px' }}>
        <p>
          <strong>Functional Problem Addressed:</strong>{' '}
          {benefit['Functional Problem Addressed'] || 'N/A'}
        </p>

        <p>
          <strong>Clinical Benefit Statement:</strong>{' '}
          {benefit['Clinical Benefit Statement'] || 'N/A'}
        </p>

        <p>
          <strong>Typical Patient Scenario:</strong>{' '}
          {benefit['Typical Patient Scenario'] || 'N/A'}
        </p>

        <button
          onClick={() =>
            addProductToDesign(
              selectedVariant,
              benefit,
              selectedJustification?.['L-CODE']
            )
          }
          style={{
            marginTop: '10px',
            padding: '8px 14px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: '#6f42c1',
            color: 'white',
            cursor: 'pointer',
            fontWeight: '600',
          }}
        >
          + Add Benefit To Design
        </button>

        <button
          onClick={() =>
            setExpandedEvidence(
              isEvidenceExpanded ? null : index
            )
          }
          disabled={supportingStudies.length === 0}
          style={{
            marginTop: '10px',
            marginLeft: '10px',
            padding: '8px 14px',
            borderRadius: '6px',
            border: '1px solid #6f42c1',
            backgroundColor: 'transparent',
            color: darkMode ? '#c9a7ff' : '#6f42c1',
            cursor:
              supportingStudies.length > 0
                ? 'pointer'
                : 'default',
            fontWeight: '600',
          }}
        >
          📚{' '}
          {isEvidenceExpanded
            ? 'Hide Supporting Evidence'
            : `View Supporting Evidence (${supportingStudies.length})`}
        </button>

	  {isEvidenceExpanded && supportingStudies.length > 0 && (
          <div
            style={{
              marginTop: '16px',
              padding: '14px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              backgroundColor: darkMode ? '#1a1a1a' : '#fafafa',
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: '14px' }}>
              Supporting Evidence
            </h4>

            {supportingStudies.map((study, studyIndex) => {
              const fullTextUrl = formatUrl(study.full_text_url);

              return (
                <div
                  key={study.study_id || study.line_id || studyIndex}
                  style={{
                    paddingBottom: '14px',
                    marginBottom:
                      studyIndex < supportingStudies.length - 1
                        ? '14px'
                        : '0',
                    borderBottom:
                      studyIndex < supportingStudies.length - 1
                        ? '1px solid #ddd'
                        : 'none',
                  }}
                >
                  <p style={{ marginTop: 0, marginBottom: '6px' }}>
                    <strong>
                      {study.title || 'Untitled Study'}
                    </strong>
                  </p>

                  <p style={{ marginTop: 0, marginBottom: '12px' }}>
                    {[study.authors, study.year, study.journal]
                      .filter(Boolean)
                      .join(' • ')}
                  </p>

                  {study.abstract_summary && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Evidence Summary:</strong>
                      <p style={{ marginTop: '4px' }}>
                        {study.abstract_summary}
                      </p>
                    </div>
                  )}

                  {study.notes && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>ProsthetIQ Notes:</strong>
                      <p style={{ marginTop: '4px' }}>
                        {study.notes}
                      </p>
                    </div>
                  )}

                  {fullTextUrl && (
                    <a
                      href={fullTextUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open Full Text ↗
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    )}
  </div>
);
})}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  };
  const renderFeedbackSection = () => {
    if (!selectedVariant) return null;

    const summaryBoxStyle = {
      border: '1px solid #d9d9d9',
      borderRadius: '10px',
      padding: '10px 12px',
      background: darkMode ? '#1f1f1f' : '#fafafa',
    };

    const cardStyle = {
      border: '1px solid #ddd',
      borderRadius: '16px',
      padding: '18px',
      background: darkMode ? '#121212' : '#ffffff',
      boxShadow: darkMode
        ? '0 2px 12px rgba(0,0,0,0.35)'
        : '0 2px 10px rgba(0,0,0,0.06)',
      height: 'fit-content',
    };

    return (
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '22px' }}>
          Clinician Feedback
        </h3>

        <p
          style={{
            marginTop: 0,
            marginBottom: '16px',
            lineHeight: 1.45,
            fontSize: '14px',
          }}
        >
          Your feedback helps guide other clinicians and improve future product
          design. You can choose to share your feedback publicly or submit it
          anonymously.
        </p>

        {!isLoggedIn && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 14px',
              borderRadius: '12px',
              border: '1px solid #ddd',
              background: darkMode ? '#1a1a1a' : '#f8f8f8',
            }}
          >
            Sign in to rate products and leave comments.
            <button
              onClick={() => goToLoginPage({ type: 'feedback' })}
              style={{
                marginLeft: '10px',
                padding: '8px 12px',
                borderRadius: '10px',
                border: '1px solid #ccc',
                cursor: 'pointer',
              }}
            >
              Sign in
            </button>
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '10px',
            marginBottom: '16px',
          }}
        >
          <div style={summaryBoxStyle}>
            <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '14px' }}>
              Likely to use again
            </div>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>
              {feedbackSummary.percentages.likely}%
            </div>
            <div style={{ opacity: 0.75, fontSize: '13px' }}>
              {feedbackSummary.counts.likely} response
              {feedbackSummary.counts.likely === 1 ? '' : 's'}
            </div>
          </div>

          <div style={summaryBoxStyle}>
            <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '14px' }}>
              Would consider using again
            </div>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>
              {feedbackSummary.percentages.consider}%
            </div>
            <div style={{ opacity: 0.75, fontSize: '13px' }}>
              {feedbackSummary.counts.consider} response
              {feedbackSummary.counts.consider === 1 ? '' : 's'}
            </div>
          </div>

          <div style={summaryBoxStyle}>
            <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '14px' }}>
              Not likely to use again
            </div>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>
              {feedbackSummary.percentages.not_likely}%
            </div>
            <div style={{ opacity: 0.75, fontSize: '13px' }}>
              {feedbackSummary.counts.not_likely} response
              {feedbackSummary.counts.not_likely === 1 ? '' : 's'}
            </div>
          </div>
        </div>

        <p style={{ marginBottom: '16px', opacity: 0.8, fontSize: '14px' }}>
          Total feedback responses: <strong>{feedbackSummary.total}</strong>
        </p>

        <div style={{ marginBottom: '18px' }}>
          <h4 style={{ marginBottom: '10px', fontSize: '16px' }}>Comments</h4>

          {visibleComments.length === 0 ? (
            <p style={{ opacity: 0.75, fontSize: '14px' }}>No comments yet.</p>
          ) : (
            <div style={{ display: 'grid', gap: '10px', maxHeight: '220px', overflowY: 'auto' }}>
              {visibleComments.map((comment) => {
                const displayName = comment.isAnonymous
                  ? 'Anonymous Clinician'
                  : commentUsers[comment.user_id] || 'Clinician';

                return (
                  <div
                    key={comment.id}
                    style={{
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      padding: '10px 12px',
                      background: darkMode ? '#1c1c1c' : '#fafafa',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        marginBottom: '4px',
                        fontSize: '14px',
                      }}
                    >
                      {displayName}
                    </div>

                    <div style={{ lineHeight: 1.45, fontSize: '14px' }}>
                      {comment.parsedText}
                    </div>

                    {comment.created_at && (
                      <div
                        style={{
                          marginTop: '6px',
                          fontSize: '12px',
                          opacity: 0.7,
                        }}
                      >
                        {new Date(comment.created_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={{
            borderTop: '1px solid #ddd',
            paddingTop: '16px',
          }}
        >
          <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '16px' }}>
            Share your experience
          </h4>

          <div style={{ display: 'grid', gap: '10px' }}>
            <select
              value={selectedFeedback}
              onChange={(e) => setSelectedFeedback(e.target.value)}
              disabled={!isLoggedIn}
              style={{
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid #ccc',
                fontSize: '14px',
                opacity: isLoggedIn ? 1 : 0.65,
              }}
            >
              <option value="">Select feedback (optional)</option>
              {FEEDBACK_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <textarea
              value={commentInput}
              onChange={(e) => setCommentInput(e.target.value)}
              placeholder="Add a comment (optional)"
              rows={3}
              disabled={!isLoggedIn}
              style={{
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid #ccc',
                fontSize: '14px',
                resize: 'vertical',
                opacity: isLoggedIn ? 1 : 0.65,
              }}
            />

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                opacity: isLoggedIn ? 1 : 0.65,
              }}
            >
              <input
                type="checkbox"
                checked={submitAnonymous}
                onChange={() => setSubmitAnonymous(!submitAnonymous)}
                disabled={!isLoggedIn}
              />
              Submit comment anonymously
            </label>

            <button
              onClick={handleSubmitClick}
              disabled={isSubmittingFeedback}
              style={{
                padding: '10px 14px',
                borderRadius: '10px',
                border: 'none',
                cursor: isSubmittingFeedback ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                fontSize: '14px',
              }}
            >
              {isSubmittingFeedback ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderVariantDetails = () => {
    if (!selectedVariant) return null;

    const variant = selectedVariant;
    const isFavorited = favoriteFamilyIds.includes(variant.product_family_id);
    const isInCompare = compareProductIds.includes(variant.id);

    const compareButtonStyle = {
      fontSize: '20px',
      borderRadius: '10px',
      padding: '6px 10px',
      border: '1px solid #ccc',
      background: isInCompare ? '#dce8ff' : 'white',
      cursor: 'pointer',
      lineHeight: 1,
    };

    const maxUserWeightLbs = getMaxUserWeightLbs(variant);
    const pdacLetterValue = String(variant['PDAC LETTER URL'] || '').trim();
    const pdacLetterUrl = ['N/A', 'NA', 'NONE', 'NULL', '-'].includes(
       pdacLetterValue.toUpperCase()
    )
     ? ''
     : formatUrl(pdacLetterValue);
    const manufacturerEducationUrl = formatUrl(variant.manufacturer_education_url);
    const manufacturerYoutubeUrl = formatUrl(variant.manufacturer_youtube_url);
    const hasEducationalResources =
  	manufacturerEducationUrl ||
  	manufacturerYoutubeUrl ||
  	variant["SPS LINK"] ||
  	variant["CASCADE LINK"] ||
  	variant["PEL LINK"];

    return (
      <section className="details-section">
        <button className="back-button" onClick={closeVariant}>
          ← Back to Variants
        </button>

        <div className="details-header">
          <img
            src={variant['PRODUCT IMAGE']}
            alt={variant['PRODUCT NAME']}
            className="details-image"
          />

          <div className="details-summary">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px',
                flexWrap: 'wrap',
              }}
            >
              <h2 style={{ margin: 0 }}>{variant['PRODUCT NAME']}</h2>

              <button
                className="favorite-button"
                onClick={(e) => toggleFavorite(e, variant)}
                title={isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}
              >
                {isFavorited ? '❤️' : '♡'}
              </button>

              <button
                onClick={(e) => toggleCompare(e, variant)}
                title={isInCompare ? 'Remove from Compare' : 'Add to Compare'}
                style={compareButtonStyle}
              >
                📋
              </button>
            </div>

            <p>
              <strong>Manufacturer:</strong> {variant['MANUFACTURER'] || 'N/A'}
            </p>
            <p>
              <strong>Family Name:</strong> {variant['family_name'] || 'N/A'}
            </p>
            <p>
              <strong>Recommended L-Codes:</strong>{' '}
              {variant['L CODES'] ||
                variant['L-CODE'] ||
                variant['L CODE'] ||
                'N/A'}
            </p>
            <p>
              <strong>PDAC Verified:</strong> {variant['PDAC VERIFIED'] || 'N/A'}
            </p>
            <p>
              <strong>Manufacturer K-Level:</strong>{' '}
              {variant['MANUFACTURER K LEVEL'] || 'N/A'}
            </p>
            <p>
              <strong>Water Capability:</strong> {variant['WATER CAPABILITY'] || 'N/A'}
            </p>
            <p>
              <strong>Special Use:</strong> {variant['SPECIAL USE'] || 'N/A'}
            </p>
            <p>
  		<strong>PDAC Letter:</strong>{' '}
  		{pdacLetterUrl ? (
    		<a href={pdacLetterUrl} target="_blank" rel="noopener noreferrer">
     		 View PDAC letter
        </a>
        ) : (
        'N/A'
        )}
           </p>
            <p>
              <strong>Website:</strong>{' '}
              {variant['WEBSITE'] ? (
                <a href={formatUrl(variant['WEBSITE'])} target="_blank" rel="noreferrer">
                  View Product Website
                </a>
              ) : (
                'N/A'
              )}
            </p>
          </div>

       {hasEducationalResources && (
  <div
    className="detail-card"
    style={{
      minWidth: '280px',
      maxWidth: '340px',
      alignSelf: 'flex-start',
    }}
  >
    <h3>Training & Availability</h3>

    {manufacturerEducationUrl && (
      <p>
        <strong>Manufacturer Training:</strong>{' '}
        <a href={manufacturerEducationUrl} target="_blank" rel="noreferrer">
          View CEUs / Training
        </a>
      </p>
    )}

    {manufacturerYoutubeUrl && (
      <p>
        <strong>YouTube Channel:</strong>{' '}
        <a href={manufacturerYoutubeUrl} target="_blank" rel="noreferrer">
          Watch Videos & Demos
        </a>
      </p>
    )}

    <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
      {variant["SPS LINK"] && (
        <a
          href={formatUrl(variant["SPS LINK"])}
          target="_blank"
          rel="noopener noreferrer"
        >
          <button>SPS ↗</button>
        </a>
      )}

      {variant["CASCADE LINK"] && (
        <a
          href={formatUrl(variant["CASCADE LINK"])}
          target="_blank"
          rel="noopener noreferrer"
        >
          <button>Cascade ↗</button>
        </a>
      )}

      {variant["PEL LINK"] && (
        <a
          href={formatUrl(variant["PEL LINK"])}
          target="_blank"
          rel="noopener noreferrer"
        >
          <button>PEL ↗</button>
        </a>
      )}
    </div>
  </div>
)}

   </div>

        <div className="details-grid">
          <div className="detail-card">
            <h3>Key Specifications</h3>
            <p>
              <strong>Product Weight Max (lbs):</strong>{' '}
              {variant['PRODUCT WEIGHT MAX lbs'] ?? 'N/A'}
            </p>
            <p>
              <strong>Product Weight Max (g):</strong>{' '}
              {variant['PRODUCT WEIGHT MAX g'] ?? 'N/A'}
            </p>
            <p>
              <strong>Build Height Max (inches):</strong>{' '}
              {variant['BUILD HEIGHT MAX inches'] ?? 'N/A'}
            </p>
            <p>
              <strong>Build Height Max (mm):</strong>{' '}
              {variant['BUILD HEIGHT MAX mm'] ?? 'N/A'}
            </p>
            <p>
              <strong>Max User Weight (lbs):</strong> {maxUserWeightLbs}
            </p>
          </div>

          <div className="detail-card">
            <h3>Product Features</h3>
            <p>{variant['KEY FEATURES'] || 'No key features listed yet.'}</p>
          </div>

          <div className="detail-card">
            <h3>L-Code Justification</h3>

            {matchingJustifications.length === 0 ? (
  		<p>No matching justifications found.</p>
	     ) : (
 	    	 matchingJustifications.map((item, index) => (
    		  <button
      		  key={index}
      		  className="justification-card"
      		  onClick={() => openJustification(item)}
    		>
     		   {item['L-CODE']}
   		   </button>
 		 ))
		)}

         </div>
        </div>

        {renderFeedbackSection()}
      </section>
    );
  };

  const renderComparePage = () => {
    const compareCardStyle = {
      border: '1px solid #ddd',
      borderRadius: '16px',
      padding: '16px',
      background: darkMode ? '#121212' : '#ffffff',
      boxShadow: darkMode
        ? '0 2px 12px rgba(0,0,0,0.35)'
        : '0 2px 10px rgba(0,0,0,0.06)',
    };

    const slotStyle = {
      border: '1px solid #ddd',
      borderRadius: '16px',
      padding: '16px',
      background: darkMode ? '#121212' : '#ffffff',
      minHeight: '130px',
      boxShadow: darkMode
        ? '0 2px 12px rgba(0,0,0,0.35)'
        : '0 2px 10px rgba(0,0,0,0.06)',
    };

    const tableCellStyle = {
      border: '1px solid #ddd',
      padding: '12px',
      verticalAlign: 'top',
    };

    return (
      <section style={{ paddingTop: '12px' }}>
        <div className="section-heading" style={{ marginBottom: '20px' }}>
          <h2>Compare List</h2>
          <p>
            {compareProducts.length} product
            {compareProducts.length === 1 ? '' : 's'} in compare
          </p>
        </div>

        <div
          style={{
            marginBottom: '18px',
            padding: '12px 14px',
            borderRadius: '12px',
            background: darkMode ? '#1a1a1a' : '#f7f8fa',
            border: '1px solid #ddd',
          }}
        >
          You can keep up to 3 products in Compare. Choose which product appears
          on the left and right, or remove one to make room for a new product.
        </div>

        {compareProducts.length === 0 ? (
          <div className="empty-state">
            No products in Compare yet. Add variants using the clipboard button.
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '16px',
                marginBottom: '24px',
              }}
            >
              {compareProducts.map((product) => {
                const isLeft = activeCompareLeftId === product.id;
                const isRight = activeCompareRightId === product.id;

                return (
                  <div key={product.id} style={compareCardStyle}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        marginBottom: '12px',
                      }}
                    >
                      <img
                        src={product['PRODUCT IMAGE']}
                        alt={product['PRODUCT NAME']}
                        style={{
                          width: '68px',
                          height: '68px',
                          objectFit: 'contain',
                          borderRadius: '10px',
                          background: darkMode ? '#1d1d1d' : '#f8f8f8',
                          border: '1px solid #ddd',
                        }}
                      />

                      <div>
                        <div style={{ fontWeight: 700, marginBottom: '4px' }}>
                          {product['PRODUCT NAME'] || 'Unnamed Product'}
                        </div>
                        <div style={{ fontSize: '14px', opacity: 0.8 }}>
                          {product['MANUFACTURER'] || 'N/A'}
                        </div>
                        <div style={{ fontSize: '13px', opacity: 0.7, marginTop: '4px' }}>
                          {product['family_name'] || 'N/A'}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                      }}
                    >
                      <button
                        onClick={() => setCompareLeft(product.id)}
                        style={{
                          padding: '8px 10px',
                          borderRadius: '10px',
                          border: '1px solid #ccc',
                          cursor: 'pointer',
                          background: isLeft ? '#dce8ff' : 'white',
                          fontWeight: isLeft ? 700 : 500,
                        }}
                      >
                        {isLeft ? 'On Left' : 'Use on Left'}
                      </button>

                      <button
                        onClick={() => setCompareRight(product.id)}
                        style={{
                          padding: '8px 10px',
                          borderRadius: '10px',
                          border: '1px solid #ccc',
                          cursor: 'pointer',
                          background: isRight ? '#dce8ff' : 'white',
                          fontWeight: isRight ? 700 : 500,
                        }}
                      >
                        {isRight ? 'On Right' : 'Use on Right'}
                      </button>

                      <button
                        onClick={(e) => toggleCompare(e, product)}
                        style={{
                          padding: '8px 10px',
                          borderRadius: '10px',
                          border: '1px solid #ccc',
                          cursor: 'pointer',
                          background: '#fff4f4',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '16px',
                marginBottom: '24px',
              }}
            >
              <div style={slotStyle}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '12px',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}
                >
                  <h3 style={{ margin: 0 }}>Left Product</h3>
                  <button
                    onClick={clearCompareLeft}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '8px',
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                      background: 'transparent',
                    }}
                  >
                    Clear Left
                  </button>
                </div>

                {activeCompareLeft ? (
                  <>
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>
                      {activeCompareLeft['PRODUCT NAME'] || 'Unnamed Product'}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.8 }}>
                      {activeCompareLeft['MANUFACTURER'] || 'N/A'}
                    </div>
                  </>
                ) : (
                  <div style={{ opacity: 0.7 }}>
                    No product selected for the left side.
                  </div>
                )}
              </div>

              <div style={slotStyle}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '12px',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}
                >
                  <h3 style={{ margin: 0 }}>Right Product</h3>
                  <button
                    onClick={clearCompareRight}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '8px',
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                      background: 'transparent',
                    }}
                  >
                    Clear Right
                  </button>
                </div>

                {activeCompareRight ? (
                  <>
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>
                      {activeCompareRight['PRODUCT NAME'] || 'Unnamed Product'}
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.8 }}>
                      {activeCompareRight['MANUFACTURER'] || 'N/A'}
                    </div>
                  </>
                ) : (
                  <div style={{ opacity: 0.7 }}>
                    No product selected for the right side.
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                overflowX: 'auto',
                borderRadius: '16px',
                boxShadow: darkMode
                  ? '0 2px 12px rgba(0,0,0,0.35)'
                  : '0 2px 10px rgba(0,0,0,0.06)',
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: darkMode ? '#121212' : '#ffffff',
                }}
              >
                <thead>
                  <tr>
                    <th style={tableCellStyle}>Specification</th>
                    <th style={tableCellStyle}>
                      {activeCompareLeft?.['PRODUCT NAME'] || 'Left Product'}
                    </th>
                    <th style={tableCellStyle}>
                      {activeCompareRight?.['PRODUCT NAME'] || 'Right Product'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={tableCellStyle}><strong>Manufacturer</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft?.['MANUFACTURER'] || '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight?.['MANUFACTURER'] || '—'}</td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Family Name</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft?.['family_name'] || '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight?.['family_name'] || '—'}</td>
                  </tr>
		<tr>
  			<td style={tableCellStyle}><strong>Key Features</strong></td>
  			<td style={tableCellStyle}>
    			{activeCompareLeft?.['KEY FEATURES'] || '—'}
  		</td>
  			<td style={tableCellStyle}>
    			{activeCompareRight?.['KEY FEATURES'] || '—'}
  		</td>
		</tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Product Weight Max (lbs)</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft?.['PRODUCT WEIGHT MAX lbs'] ?? '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight?.['PRODUCT WEIGHT MAX lbs'] ?? '—'}</td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Product Weight Max (g)</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft?.['PRODUCT WEIGHT MAX g'] ?? '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight?.['PRODUCT WEIGHT MAX g'] ?? '—'}</td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Build Height Max (inches)</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft?.['BUILD HEIGHT MAX inches'] ?? '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight?.['BUILD HEIGHT MAX inches'] ?? '—'}</td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Build Height Max (mm)</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft?.['BUILD HEIGHT MAX mm'] ?? '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight?.['BUILD HEIGHT MAX mm'] ?? '—'}</td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Max User Weight (lbs)</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft ? getMaxUserWeightLbs(activeCompareLeft) : '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight ? getMaxUserWeightLbs(activeCompareRight) : '—'}</td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Water Capability</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft?.['WATER CAPABILITY'] || '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight?.['WATER CAPABILITY'] || '—'}</td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>PDAC Verified</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft?.['PDAC VERIFIED'] || '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight?.['PDAC VERIFIED'] || '—'}</td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Manufacturer K-Level</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft?.['MANUFACTURER K LEVEL'] || '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight?.['MANUFACTURER K LEVEL'] || '—'}</td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Recommended L-Codes</strong></td>
                    <td style={tableCellStyle}>
                      {activeCompareLeft?.['L CODES'] ||
                        activeCompareLeft?.['L-CODE'] ||
                        activeCompareLeft?.['L CODE'] ||
                        '—'}
                    </td>
                    <td style={tableCellStyle}>
                      {activeCompareRight?.['L CODES'] ||
                        activeCompareRight?.['L-CODE'] ||
                        activeCompareRight?.['L CODE'] ||
                        '—'}
                    </td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Special Use</strong></td>
                    <td style={tableCellStyle}>{activeCompareLeft?.['SPECIAL USE'] || '—'}</td>
                    <td style={tableCellStyle}>{activeCompareRight?.['SPECIAL USE'] || '—'}</td>
                  </tr>
                  <tr>
                    <td style={tableCellStyle}><strong>Website</strong></td>
                    <td style={tableCellStyle}>
                      {activeCompareLeft?.['WEBSITE'] ? (
                        <a
                          href={formatUrl(activeCompareLeft['WEBSITE'])}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View Product Website
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      {activeCompareRight?.['WEBSITE'] ? (
                        <a
                          href={formatUrl(activeCompareRight['WEBSITE'])}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View Product Website
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    );
  };

  const renderVariantList = () => {
    if (!selectedFamily) return null;

    const variants = [...(selectedFamily.variants || [])].sort((a, b) =>
      String(a['PRODUCT NAME'] || '').localeCompare(
        String(b['PRODUCT NAME'] || '')
      )
    );

    return (
      <section className="product-section">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
  <button className="back-button" onClick={closeFamily}>
    ← Back to All Products
  </button>

  {cameFromKyan && (
    <button
      onClick={() => setActivePage('Kyan')}
      style={{
        padding: '6px 10px',
        borderRadius: '8px',
        border: '1px solid #ccc',
        cursor: 'pointer',
        fontSize: '12px',
        background: darkMode ? '#111111' : '#ffffff',
      }}
    >
      ← Back to Kyan Results
    </button>
  )}
</div>

        <div className="section-heading">
          <h2>{selectedFamily.display_name}</h2>
          <p>{variants.length} variants</p>
        </div>

        <div className="product-grid">
          {variants.map((variant) => {
            const isFavorited = favoriteFamilyIds.includes(variant.product_family_id);
            const isInCompare = compareProductIds.includes(variant.id);

            const compareButtonStyle = {
              position: 'absolute',
              top: '12px',
              right: '52px',
              zIndex: 2,
              fontSize: '18px',
              borderRadius: '10px',
              padding: '4px 8px',
              border: '1px solid #ccc',
              background: isInCompare ? '#dce8ff' : 'white',
              cursor: 'pointer',
              lineHeight: 1,
            };

            return (
              <div
                key={variant.id || variant['PRODUCT NAME']}
                className="product-card"
                onClick={() => openVariant(variant)}
                style={{ position: 'relative' }}
              >
                <button
                  style={compareButtonStyle}
                  onClick={(e) => toggleCompare(e, variant)}
                  title={isInCompare ? 'Remove from Compare' : 'Add to Compare'}
                >
                  📋
                </button>

                <button
                  className="favorite-button"
                  onClick={(e) => toggleFavorite(e, variant)}
                  title={isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}
                >
                  {isFavorited ? '❤️' : '♡'}
                </button>

                <div className="product-image-wrap">
                  <img
                    src={variant['PRODUCT IMAGE']}
                    alt={variant['PRODUCT NAME']}
                    className="product-image"
                  />
                </div>

                <p className="product-name">{variant['PRODUCT NAME']}</p>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

if (!authChecked && !authUser && !currentUserRow) {
  return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      Loading ProsthetIQ...
    </div>
  );
}

return (
    <>
      <div className={darkMode ? 'App dark' : 'App'}>
        {menuOpen && <aside className="sidebar">
          <h2>ProsthetIQ</h2>

          <button
            className={`sidebar-link ${activePage === 'Kyan' ? 'active' : ''}`}
            onClick={() => goToPage('Kyan')}
          >
            Kyan ✨
          </button>

          <button
            className={`sidebar-link ${activePage === 'All Products' ? 'active' : ''}`}
            onClick={() => goToPage('All Products')}
          >
            All Products
          </button>

          <button
            className={`sidebar-link ${activePage === 'Feet' ? 'active' : ''}`}
            onClick={() => goToPage('Feet')}
          >
            Feet
          </button>

          <button
            className={`sidebar-link ${activePage === 'Knees' ? 'active' : ''}`}
            onClick={() => goToPage('Knees')}
          >
            Knees
          </button>

          <button
            className={`sidebar-link ${activePage === 'Hips' ? 'active' : ''}`}
            onClick={() => goToPage('Hips')}
          >
            Hips
          </button>

          <button
  	className={`sidebar-link ${activePage === 'Integrated Systems' ? 'active' : ''}`}
  	onClick={() => goToPage('Integrated Systems')}
	>
  	Integrated Systems
	</button>

	<hr />

	<button
  	className={`sidebar-link ${activePage === 'Favorites' ? 'active' : ''}`}
  	onClick={() => handleProtectedPageNav('Favorites')}
	>
  	Favorites
	</button>

          <button
            className={`sidebar-link ${activePage === 'Compare List' ? 'active' : ''}`}
            onClick={() => handleProtectedPageNav('Compare List')}
          >
            Compare List
          </button>

          <hr />

                    <button
            className={`sidebar-link ${activePage === 'Tutorials' ? 'active' : ''}`}
            onClick={() => goToPage('Tutorials')}
          >
            Help & Tutorials
          </button>

          <a
            className="sidebar-link"
            href="https://prosthetiq-logic.vercel.app/"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
          >
            ProsthetIQ Logic ↗
          </a>

	  {isLoggedIn && (
 	    <a
   	    className="sidebar-link"
    	    href="https://prosthetiq-design.vercel.app/"
    	    target="_blank"
    	    rel="noreferrer"
    	    style={{ textDecoration: 'none' }}
  	  >
    	    ProsthetIQ Design ↗
  	</a>
	)}

        </aside>}

        <main className="main-content">
          <header className="header">
            <div className="header-left">
<button
  onClick={() => setMenuOpen(!menuOpen)}
  style={{
    fontSize: '24px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    marginRight: '10px'
  }}
>
  ☰
</button>
              <h2>ProsthetIQ</h2>
            </div>

            <div className="header-center">
              {activePage !== 'Kyan' &&
                activePage !== 'Compare List' &&
                activePage !== 'Login' && (
                  <input
                    type="text"
                    className="header-search"
                    placeholder="Search products, manufacturers, L-codes, or features..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                )}
            </div>

            <div className="header-right">
              {isLoggedIn ? (
                <>
                  <span style={{ marginRight: '12px', fontSize: '14px' }}>
                    {getUserDisplayName(currentUserRow)}
                  </span>
                  <button onClick={handleLogout} style={{ marginRight: '12px' }}>
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => goToLoginPage()}
                  style={{ marginRight: '12px' }}
                >
                  Login
                </button>
              )}

              <span className="mode-label">
                {darkMode ? 'Dark Mode' : 'Light Mode'}
              </span>

              <label className="switch">
                <input
                  type="checkbox"
                  checked={darkMode}
                  onChange={() => setDarkMode(!darkMode)}
                />
                <span className="slider"></span>
              </label>
            </div>
          </header>

          {activePage === 'Login' && renderLoginPage()}

          {activePage === 'Kyan' && (
            <section className="kyan-hero">
              <img src={logo} alt="ProsthetIQ logo" className="kyan-logo" />

              <h1>Hi, I’m Kyan.</h1>

              <p className="kyan-subtitle">
                Tell me what you’re working on and I’ll help you find the right fit.
              </p>

<div style={{ marginTop: '14px', marginBottom: '14px', textAlign: 'center' }}>
  <div style={{ marginBottom: '12px' }}>
    <div style={{ fontWeight: 700, marginBottom: '6px' }}>
      Feet
    </div>

    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '8px',
      }}
    >
      {KYAN_PROMPT_CHIPS.slice(0, 4).map((chip) => (
        <button
          key={chip.label}
          type="button"
          onClick={() => {
            setKyanPrompt(chip.prompt);
            setExpandedKyanGroup(null);
            setKyanRefinements({});
            setKyanBaseCodeFilters({});
          }}
          style={{
            padding: '7px 12px',
            borderRadius: '999px',
            border: darkMode ? '1px solid #444' : '1px solid #d0d0d0',
            background: darkMode ? '#1f1f1f' : '#f7f7f7',
            color: darkMode ? '#ffffff' : '#333333',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {chip.label}
        </button>
      ))}
    </div>
  </div>

  <div>
    <div style={{ fontWeight: 700, marginBottom: '6px' }}>
      Knees
    </div>

    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '8px',
      }}
    >
      {KYAN_PROMPT_CHIPS.slice(4).map((chip) => (
        <button
          key={chip.label}
          type="button"
          onClick={() => {
            setKyanPrompt(chip.prompt);
            setExpandedKyanGroup(null);
            setKyanRefinements({});
            setKyanBaseCodeFilters({});
          }}
          style={{
            padding: '7px 12px',
            borderRadius: '999px',
            border: darkMode ? '1px solid #444' : '1px solid #d0d0d0',
            background: darkMode ? '#1f1f1f' : '#f7f7f7',
            color: darkMode ? '#ffffff' : '#333333',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {chip.label}
        </button>
      ))}
    </div>
  </div>
</div>

<div
  style={{
    fontSize: '13px',
    opacity: 0.7,
    marginBottom: '10px',
    textAlign: 'center',
  }}
>
  Choose a starting point, or describe your patient below.
</div>

              <textarea
  		className="kyan-textbox"
  		placeholder="Enter K-level, amputation level, device type, patient weight, and desired feature (example: shock absorption)"
  		value={kyanPrompt}
  		onChange={(e) => {
  		 setKyanPrompt(e.target.value);
  		 setExpandedKyanGroup(null);
  		 setKyanRefinements({});
  		 setKyanBaseCodeFilters({});
		}}
	       />

		<div className="prompt-hint">
  		Example: “K3 transtibial shock absorption foot”
	       </div>
<div
  style={{
    marginTop: '14px',
    textAlign: 'center',
    fontSize: '14px',
    lineHeight: 1.5,
  }}
>
  <div style={{ opacity: 0.75 }}>
    💬 Want real-world input from other clinicians?
  </div>

  <a
    href="https://discord.gg/DQgcVmeYvN"
    target="_blank"
    rel="noopener noreferrer"
    style={{
      display: 'inline-block',
      marginTop: '6px',
      fontWeight: 700,
      color: '#4f8cff',
      textDecoration: 'none',
    }}
  >
    Join the ProsthetIQ Community ↗
  </a>
</div>

		{kyanResults.length > 0 && (
  <div style={{ marginTop: '20px' }}>
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px',
    }}
  >
    <h3 style={{ margin: 0 }}>Kyan’s Suggestions</h3>

    <button
      onClick={clearKyan}
      style={{
        padding: '6px 10px',
        borderRadius: '10px',
        border: '1px solid #ccc',
        cursor: 'pointer',
        background: darkMode ? '#111111' : '#ffffff',
        fontSize: '12px',
      }}
    >
      Clear All
    </button>
  </div>

  <div style={{ display: 'grid', gap: '16px' }}>
    {kyanResults.map((group, groupIndex) => {
      const selectedGroup = [
  ...Object.values(FOOT_KYAN_GROUPS),
  ...Object.values(KNEE_KYAN_GROUPS),
  ...Object.values(HIP_KYAN_GROUPS),
].find((item) => item.title === group.title);

const isConfiguredGroup = !!selectedGroup;

      return (
  <div
    key={groupIndex}
        style={{
      border: '1px solid #ddd',
      borderRadius: '14px',
      padding: '16px',
      cursor: 'default',
      background: darkMode ? '#1a1a1a' : '#ffffff',
      boxShadow: darkMode
        ? '0 2px 10px rgba(0,0,0,0.25)'
        : '0 2px 8px rgba(0,0,0,0.06)',
    }}
  >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '12px',
              marginBottom: '10px',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: '18px' }}>{group.title}</div>

              {isConfiguredGroup ? (
  <>
    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '4px' }}>
      <strong>Associated L-codes:</strong>{' '}
      {(
  group.title === 'Stability / Balance Feet'
    ? [
        { label: 'SACH (L5970/L5971)', codes: ['L5970', 'L5971'] },
        { label: 'L5972', codes: ['L5972'] },
        { label: 'L5974', codes: ['L5974'] },
      ].filter((item) => {
        if (!group.defaultBaseCodes || group.defaultBaseCodes.length === 0) {
          return true;
        }

        return item.codes.some((code) =>
          group.defaultBaseCodes.includes(code)
        );
      })
    : group.title === 'Stability & Pelvic Control Hips'
  	? [{ label: 'L5331/L5341', codes: ['L5331', 'L5341'] }]
  	: selectedGroup.codes.map((code) => ({ label: code, codes: [code] }))
    ).map((item) => {
        const active =
  	JSON.stringify(kyanBaseCodeFilters[groupIndex]) === JSON.stringify(item.codes);

       return (
  <button
    key={item.label}
    onClick={() =>
      setKyanBaseCodeFilters((prev) => ({
        ...prev,
        [groupIndex]: active ? null : item.codes,
      }))
    }
    style={{
      marginLeft: '6px',
      padding: '4px 8px',
      borderRadius: '8px',
      border: '1px solid #4da6ff',
      background: active ? '#cfe8ff' : 'transparent',
      color: '#007bff',
      cursor: 'pointer',
      fontSize: '12px',
    }}
  >
    {item.label}
  </button>
);
      })}
    </div>

    <div style={{ fontSize: '14px', opacity: 0.8, marginTop: '4px' }}>
      {selectedGroup.description}
    </div>
  </>
) : (
  <div style={{ fontSize: '14px', opacity: 0.8 }}>
    {group.products.length} matching products
  </div>
)}

            {isConfiguredGroup && (
              <button
                onClick={() =>
                  setExpandedKyanGroup(expandedKyanGroup === groupIndex ? null : groupIndex)
                }
                style={{
  			background: 'none',
  			border: 'none',
  			cursor: 'pointer',
  			fontWeight: 600,
  			fontSize: '14px',
  			color: darkMode ? '#ffffff' : '#000000',
  			display: 'block',
  			marginLeft: 'auto',
  			marginTop: '8px',
		}}
              >
                {expandedKyanGroup === groupIndex ? 'Hide details ▲' : 'Show details ▼'}
              </button>
            )}
            </div>
          </div>

          {isConfiguredGroup && (
            <>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>Refine this result:</div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {[
  			{ key: 'standard', label: 'Standard' },
  			{ key: 'multiaxial', label: '+ Multiaxial' },
  			{ key: 'elevated_vacuum', label: '+ Elevated Vacuum' },
  			{ key: 'hydraulic', label: '+ Hydraulic Ankle' },
  			{ key: 'adjustable', label: '+ Adjustable Ankle' },

  			{ key: 'shock_absorption', label: '+ Stance Flexion' },
  			{ key: 'variable_cadence', label: '+ Extension Dampening' },
  			{ key: 'limb_advancement', label: '+ Swing Assistance / Limb Advancement' },
  			{ key: 'microprocessor', label: '+ Advanced Control / Microprocessor' },
  			{ key: 'manual_lock', label: '+ Manual Lock' },
			{ key: 'flexion_assist', label: '+ Flexion Assist' },
			{ key: 'hydraulic_control', label: '+ Hydraulic Control Systems' },
  			{ key: 'integrated_system', label: '+ Integrated System' },
  			{ key: 'powered', label: '+ Powered / Motor Control' },
		]
  		  .filter((option) => {
  		   if (
    			group.title === 'Stability / Balance Feet' &&
    			group.defaultBaseCodes &&
    			group.defaultBaseCodes.length > 0 &&
    			group.defaultBaseCodes.every((code) =>
      			  ['L5970', 'L5971', 'L5974'].includes(code)
    		)
  		) {
   		 return option.key === 'standard';
  		}

  		return (group.refinements || []).includes(option.key);
		})
  		  .map((option) => {
                  const active = kyanRefinements[groupIndex] === option.key;

                  return (
                    <button
                      key={option.key}
                      onClick={() =>
                        setKyanRefinements((prev) => ({
                          ...prev,
                          [groupIndex]: active ? null : option.key,
                        }))
                      }
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid #7aa7c7',
                        background: active ? '#dceeff' : darkMode ? '#111111' : '#ffffff',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {(!isConfiguredGroup || expandedKyanGroup === groupIndex) && (
            <>
              {isConfiguredGroup && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: '10px' }}>
                    {group.products.filter((product) => {
const selectedBaseCode = kyanBaseCodeFilters[groupIndex];
const selectedBaseCodes = Array.isArray(selectedBaseCode)
  ? selectedBaseCode
  : selectedBaseCode
  ? [selectedBaseCode]
  : group.defaultBaseCodes || [];

if (selectedBaseCodes.length > 0) {
  const codes = [
    ...(product.matchingVariants || [product]).flatMap((variant) =>
      getVariantLCodes(variant)
    ),
  ];

  const matchesSelectedBaseCode = selectedBaseCodes.some((code) =>
    codes.includes(code)
  );

  if (!matchesSelectedBaseCode) return false;
}
const selectedRefinement = kyanRefinements[groupIndex];
if (!selectedRefinement) return true;
  const allCodes = [
    ...(product.matchingVariants || [product]).flatMap((variant) =>
      getVariantLCodes(variant)
    ),
  ];

  const combinedText = [
    product['family_name'],
    product['PRODUCT NAME'],
    ...(product.matchingVariants || []).map((v) => v['PRODUCT NAME']),
  ]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();

  const isElevatedVacuum =
  allCodes.includes('L5781') ||
  combinedText.includes('EVAQ8') ||
  combinedText.includes('UNITY') ||
  combinedText.includes('HARMONY') ||
  combinedText.includes('VAC') ||
  combinedText.includes('VACUUM') ||
  combinedText.includes('IVT');

  const hasBaseCode = selectedGroup.codes.some(code => allCodes.includes(code));
    const hasL5984 = allCodes.includes('L5984');
    const hasL5986 = allCodes.includes('L5986');
    const hasL5968 = allCodes.includes('L5968');
    const hasL5990 = allCodes.includes('L5990');
    const hasL5845 = allCodes.includes('L5845');

 const isKneeGroup = normalizeText(selectedGroup.title).includes('KNEE');
 const isHipGroup = normalizeText(selectedGroup.title).includes('HIP');

 const hasL5848 = allCodes.includes('L5848');
 const hasL5850 = allCodes.includes('L5850');
 const hasL5856 = allCodes.includes('L5856');
 const hasL5857 = allCodes.includes('L5857');
 const hasL5925 = allCodes.includes('L5925');
 const hasL5855 = allCodes.includes('L5855');
 const hasL5961 = allCodes.includes('L5961');

if (isKneeGroup && selectedRefinement === 'standard') {
  return (
    hasBaseCode &&
    !hasL5845 &&
    !hasL5848 &&
    !hasL5850 &&
    !hasL5856 &&
    !hasL5857 &&
    !hasL5925
  );
}

if (isKneeGroup && selectedRefinement === 'shock_absorption') {
  return hasBaseCode && hasL5845;
}

if (isKneeGroup && selectedRefinement === 'variable_cadence') {
  return hasBaseCode && hasL5848;
}

if (isKneeGroup && selectedRefinement === 'limb_advancement') {
  return hasBaseCode && hasL5850;
}

if (isKneeGroup && selectedRefinement === 'microprocessor') {
  return hasBaseCode && (hasL5856 || hasL5857);
}

if (isKneeGroup && selectedRefinement === 'manual_lock') {
  return hasBaseCode && hasL5925;
}

if (isKneeGroup && selectedRefinement === 'integrated_system') {
  const productDeviceType = normalizeText(product['DEVICE TYPE']);

  return hasBaseCode && productDeviceType === 'INTEGRATED SYSTEM';
}

if (isHipGroup && selectedRefinement === 'flexion_assist') {
  return hasBaseCode && hasL5855;
}

if (isHipGroup && selectedRefinement === 'hydraulic_control') {
  const isHydraulicHip =
    hasL5961 ||
    combinedText.includes('HYDRAULIC');

  return hasBaseCode && isHydraulicHip;
}

if (isHipGroup && selectedRefinement === 'manual_lock') {
  return hasBaseCode && hasL5925;
}

if (selectedRefinement === 'elevated_vacuum') return isElevatedVacuum;

if (selectedRefinement === 'multiaxial') {
  return hasBaseCode && (hasL5984 || hasL5986) && !isElevatedVacuum;
}

if (selectedRefinement === 'hydraulic') {
  return hasBaseCode && hasL5968 && !isElevatedVacuum;
}

if (selectedRefinement === 'adjustable') {
  return hasBaseCode && hasL5990;
}

if (selectedRefinement === 'standard') {
  return hasBaseCode && !hasL5984 && !hasL5986 && !hasL5968 && !hasL5990 && !isElevatedVacuum;
}

return true;
}).length} matching products
                  </div>

                  <div
                    style={{
                      borderTop: '1px solid #e5e5e5',
                      paddingTop: '14px',
                      marginTop: '6px',
                      marginBottom: '14px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: '8px' }}>
                      Clinical considerations:
                    </div>

                    <ul
                      style={{
                        display: 'inline-block',
                        textAlign: 'left',
                        margin: 0,
                        paddingLeft: '20px',
                        fontSize: '14px',
                        lineHeight: 1.6,
                      }}
                    >
                      <li>May reduce loading forces during gait.</li>
                      <li>Can improve comfort with prolonged walking.</li>
                      <li>Useful when shock absorption is a primary clinical goal.</li>
                    </ul>

                    <div style={{ fontWeight: 700, marginTop: '10px' }}>
                      Matching products:
                    </div>
                  </div>
                </>
              )}

              <div style={{ display: 'grid', gap: '10px' }}>
                {group.products
                  .filter((product) => {
                    if (!isConfiguredGroup) return true;
		const selectedBaseCode = kyanBaseCodeFilters[groupIndex];
		const selectedBaseCodes = Array.isArray(selectedBaseCode)
  		 ? selectedBaseCode
  		 : selectedBaseCode
  		 ? [selectedBaseCode]
  		 : [];

		if (selectedBaseCodes.length > 0) {
  		 const codes = [
    		...(product.matchingVariants || [product]).flatMap((variant) =>
      		getVariantLCodes(variant)
    		),
  		];

  		const matchesSelectedBaseCode = selectedBaseCodes.some((code) =>
   		 codes.includes(code)
  		);

  		if (!matchesSelectedBaseCode) return false;
		}

                    const selectedRefinement = kyanRefinements[groupIndex];
                    if (!selectedRefinement) return true;

                    const combinedText = [
                      product['family_name'],
                      product['PRODUCT NAME'],
                      ...(product.matchingVariants || []).map((v) => v['PRODUCT NAME']),
                    ]
                      .filter(Boolean)
                      .join(' ')
                      .toUpperCase();

                    const allCodes = [
                      ...(product.matchingVariants || [product]).flatMap((variant) =>
                        getVariantLCodes(variant)
                      ),
                    ];


const uniqueCodes = allCodes.filter((code, idx, arr) => arr.indexOf(code) === idx);

const isElevatedVacuum =
  uniqueCodes.includes('L5781') ||
  combinedText.includes('EVAQ8') ||
  combinedText.includes('UNITY') ||
  combinedText.includes('HARMONY') ||
  combinedText.includes('VAC') ||
  combinedText.includes('VACUUM') ||
  combinedText.includes('IVT');

const hasBaseCode = selectedGroup.codes.some((code) => uniqueCodes.includes(code));
const hasL5984 = uniqueCodes.includes('L5984');
const hasL5986 = uniqueCodes.includes('L5986');
const hasL5968 = uniqueCodes.includes('L5968');
const hasL5990 = uniqueCodes.includes('L5990');
const hasL5845 = uniqueCodes.includes('L5845');

const isKneeGroup = normalizeText(selectedGroup.title).includes('KNEE');
const isHipGroup = normalizeText(selectedGroup.title).includes('HIP');

const hasL5848 = uniqueCodes.includes('L5848');
const hasL5850 = uniqueCodes.includes('L5850');
const hasL5856 = uniqueCodes.includes('L5856');
const hasL5857 = uniqueCodes.includes('L5857');
const hasL5925 = uniqueCodes.includes('L5925');
const hasL5855 = uniqueCodes.includes('L5855');
const hasL5961 = uniqueCodes.includes('L5961');

/* =========================
   KNEE REFINEMENTS FIRST
========================= */

if (isKneeGroup && selectedRefinement === 'standard') {
  return (
    hasBaseCode &&
    !hasL5845 &&
    !hasL5848 &&
    !hasL5850 &&
    !hasL5856 &&
    !hasL5857 &&
    !hasL5925
  );
}

if (isKneeGroup && selectedRefinement === 'shock_absorption') {
  return hasBaseCode && hasL5845;
}

if (isKneeGroup && selectedRefinement === 'variable_cadence') {
  return hasBaseCode && hasL5848;
}

if (isKneeGroup && selectedRefinement === 'limb_advancement') {
  return hasBaseCode && hasL5850;
}

if (isKneeGroup && selectedRefinement === 'microprocessor') {
  return hasBaseCode && (hasL5856 || hasL5857);
}

if (isKneeGroup && selectedRefinement === 'manual_lock') {
  return hasBaseCode && hasL5925;
}

if (isKneeGroup && selectedRefinement === 'integrated_system') {
  const hasL5973 = uniqueCodes.includes('L5973');
  const hasAqualimbTfCombo =
    uniqueCodes.includes('L5925') &&
    uniqueCodes.includes('L5984') &&
    uniqueCodes.includes('L5972');

  return hasBaseCode && (hasL5973 || hasAqualimbTfCombo);
}

if (isHipGroup && selectedRefinement === 'flexion_assist') {
  return hasBaseCode && hasL5855;
}

if (isHipGroup && selectedRefinement === 'hydraulic_control') {
  const isHydraulicHip =
    hasL5961 ||
    combinedText.includes('HYDRAULIC');

  return hasBaseCode && isHydraulicHip;
}

if (isHipGroup && selectedRefinement === 'manual_lock') {
  return hasBaseCode && hasL5925;
}

/* =========================
   FOOT LOGIC (UNCHANGED)
========================= */

if (selectedRefinement === 'elevated_vacuum') {
  return isElevatedVacuum;
}

if (selectedRefinement === 'multiaxial') {
  return hasBaseCode && (hasL5984 || hasL5986) && !isElevatedVacuum;
}

if (selectedRefinement === 'standard') {
  return hasBaseCode && !hasL5984 && !hasL5986 && !hasL5968 && !hasL5990 && !isElevatedVacuum;
}

if (selectedRefinement === 'hydraulic') {
  return hasBaseCode && hasL5968 && !isElevatedVacuum;
}

if (selectedRefinement === 'adjustable') {
  return hasBaseCode && hasL5990;
}

return true;
})
.map((product, index) => {
                    const name =
                      product['family_name'] ||
                      product['PRODUCT NAME'] ||
                      product.name ||
                      'Unnamed Product';

                    const variants = product.matchingVariants || [product];

                    return (
                      <div
  			key={index}
  			onClick={(e) => {
  			  e.stopPropagation();

  			  setCameFromKyan(true); 

  			  setActivePage('All Products');

			setTimeout(() => {
 			 openFamily({
 			   ...product,
			    variants: product.matchingVariants || [product],
			    display_name:
			      product.family_name || product['PRODUCT NAME'] || product.name || 'Unnamed Product',
 			   familyId: product.product_family_id,
			  });
			}, 0);
  			}}
  			style={{
   			 border: '1px solid #e5e5e5',
    			 borderRadius: '12px',
    			 padding: '12px',
    			 background: darkMode ? '#121212' : '#fafafa',
    			 textAlign: 'center',
   			 cursor: 'pointer',
  		   }}
	         >
                        <div style={{ fontWeight: 700, marginBottom: '4px' }}>{name}</div>

                        <div style={{ fontSize: '13px', opacity: 0.8 }}>
                          {variants[0]?.['MANUFACTURER'] || ''}
                        </div>

                        <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>
                          {variants
                            .flatMap((variant) => getVariantLCodes(variant))
                            .filter(Boolean)
                            .filter((code, idx, arr) => arr.indexOf(code) === idx)
                            .join(', ')}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>
            );
     })}
  </div>
  </div>
)}
</section>
)}

          {activePage === 'Tutorials' && (
            <section style={{ paddingTop: '12px' }}>
              <div className="section-heading" style={{ marginBottom: '24px' }}>
                <h2>Help & Tutorials</h2>
              </div>

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <a
                  href="https://www.youtube.com/@ProsthetIQ"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    padding: '14px 20px',
                    borderRadius: '12px',
                    background: '#2f5d6b',
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: '600',
                  }}
                >
                  ProsthetIQ YouTube Channel
                </a>

                <a
                  href="https://sites.google.com/view/prosthetiq/ip-water-ratings-guide"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    padding: '14px 20px',
                    borderRadius: '12px',
                    background: '#2f5d6b',
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: '600',
                  }}
                >
                  Water Resistance Guide
                </a>
              </div>
            </section>
          )}

          {activePage === 'Compare List' &&
            !selectedFamily &&
            !selectedVariant &&
            renderComparePage()}

          {activePage !== 'Kyan' &&
            activePage !== 'Tutorials' &&
            activePage !== 'Compare List' &&
            activePage !== 'Login' &&
            !selectedFamily &&
            !selectedVariant && (
              <>
                <div className="filter-row">
                  <select
                    value={clinicalFilter}
                    onChange={(e) => setClinicalFilter(e.target.value)}
                  >
                    {CLINICAL_FILTER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={lCodeFilter}
                    onChange={(e) => setLCodeFilter(e.target.value)}
                  >
                    <option value="all">L-code</option>
                    {lCodes.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    placeholder="Patient Weight (lbs)"
                    value={patientWeight}
                    onChange={(e) => setPatientWeight(e.target.value)}
                  />

                  <select
                    value={manufacturerFilter}
                    onChange={(e) => setManufacturerFilter(e.target.value)}
                  >
                    <option value="all">Manufacturer</option>
                    {manufacturers.map((manufacturer) => (
                      <option key={manufacturer} value={manufacturer}>
                        {manufacturer}
                      </option>
                    ))}
                  </select>
                </div>

                <section className="product-section">
                  <div className="section-heading">
                    <h2>{pageTitle}</h2>
                    <p>{familyProducts.length} results</p>
                  </div>

                  {familyProducts.length === 0 && (
                    <p className="empty-state">
                      {activePage === 'Favorites'
                        ? 'No favorites saved yet.'
                        : 'No products found for those filters.'}
                    </p>
                  )}

                  <div className="product-grid">
                    {currentProducts.map((product, index) => {
                      const productName =
                        product.display_name ||
                        product['family_name'] ||
                        product.name ||
                        product['PRODUCT NAME'] ||
                        'Unnamed Product';

                      const isFavorited = favoriteFamilyIds.includes(product.product_family_id);

                      return (
                        <div
                          key={`${product.familyId || 'unknown'}-${productName}-${index}`}
                          className="product-card"
                          onClick={() => openFamily(product)}
                        >
                          <button
                            className="favorite-button"
                            onClick={(e) => toggleFavorite(e, product)}
                            title={isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}
                          >
                            {isFavorited ? '❤️' : '♡'}
                          </button>

                          <div className="product-image-wrap">
                            <img
                              src={product['PRODUCT IMAGE']}
                              alt={productName}
                              className="product-image"
                            />
                          </div>

                          <p className="product-name">{productName}</p>
                        </div>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>

                      {Array.from({ length: totalPages }, (_, index) => (
                        <button
                          key={index + 1}
                          className={currentPage === index + 1 ? 'active-page' : ''}
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      ))}

                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </section>
              </>
            )}

          {activePage !== 'Kyan' &&
            activePage !== 'Tutorials' &&
            activePage !== 'Compare List' &&
            activePage !== 'Login' &&
            selectedFamily &&
            !selectedVariant &&
            renderVariantList()}

          {activePage !== 'Kyan' &&
            activePage !== 'Tutorials' &&
            activePage !== 'Compare List' &&
            activePage !== 'Login' &&
            selectedVariant &&
            !selectedJustification &&
            renderVariantDetails()}

          {activePage !== 'Kyan' &&
            activePage !== 'Tutorials' &&
            activePage !== 'Compare List' &&
            activePage !== 'Login' &&
            selectedJustification &&
            renderJustificationDetails()}

          {showNegativeModal && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px',
              }}
            >
              <div
                style={{
                  width: '100%',
                  maxWidth: '560px',
                  borderRadius: '16px',
                  padding: '24px',
                  background: darkMode ? '#181818' : '#ffffff',
                  color: darkMode ? '#ffffff' : '#000000',
                }}
              >
                <h3 style={{ marginTop: 0 }}>Tell us more</h3>
                <p style={{ lineHeight: 1.5 }}>
                  You selected “Not likely to use again.” You can still submit that
                  feedback, and adding a comment would be especially helpful.
                </p>

                <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                  <button
                    onClick={submitFeedback}
                    disabled={isSubmittingFeedback}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: 'none',
                      cursor: isSubmittingFeedback ? 'not-allowed' : 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    {isSubmittingFeedback ? 'Submitting...' : 'Submit anyway'}
                  </button>

                  <button
                    onClick={() => setShowNegativeModal(false)}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '10px',
                      border: '1px solid #ccc',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    Go back
                  </button>
                </div>
              </div>
            </div>
          )}

                    <footer
            style={{
              marginTop: '24px',
              padding: '12px 0',
              borderTop: darkMode ? '1px solid #444' : '1px solid #ddd',
              color: darkMode ? '#cccccc' : '#555555',
              fontSize: '13px',
              lineHeight: 1.4,
              textAlign: 'center',
            }}
          >
            <p style={{ margin: 0 }}>
              ProsthetIQ is for educational purposes only and does not replace
              clinical judgment. Information may change; accuracy and completeness
              are not guaranteed. Verify specifications, compatibility,
              suitability, and current instructions for use with the manufacturer.
	      <br />
              Coding does not guarantee coverage or reimbursement; confirm PDAC
              verification when applicable and insurer requirements.
            </p>
          </footer>

        </main>
      </div>
    </>
  );
}

export default App;